/**
 * The RFQ workspace: upload a document (PDF, Excel, image, Word,
 * PowerPoint...), have it read into structured material lines, fill whatever
 * the document did not say, save it for later — complete or not — and submit
 * it for a quote.
 *
 * The left panel lists saved RFQs by date and first material, each deletable
 * and resumable. Sign-in is required before processing: the upload survives
 * the round trip through /signin in sessionStorage where it fits.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import {
  ArrowUpTrayIcon,
  CheckCircleIcon,
  ClipboardDocumentIcon,
  ExclamationTriangleIcon,
  PlusIcon,
  TrashIcon,
} from "@heroicons/react/24/outline";
import useFirebaseAuthentication from "../common/hooks/useFirebaseAuthentication";
import { EmailThreadPanel, NextStepCard } from "./CommunicationBoard";
import {
  ActivityEntry,
  ActivityTrail,
  activityLine,
  actorLabel,
} from "../components/ActivityTrail";
import {
  AddressFormat,
  formatAddress,
  sendAuthenticatedRequest,
} from "../common/functions";
import AddressAutocomplete from "../common/components/AddressAutocomplete";
import { Address } from "../common/models/company";
import { CustomerProfile } from "../common/models/customerFields";
import {
  BetterPrice,
  ContactUsScanResult,
  EmailThread,
  DIMENSIONS_BY_SHAPE,
  Rfq,
  RfqCustomer,
  RfqLine,
  RfqLineOptions,
  RfqPriority,
  RfqQuoteAttempt,
  RfqRemovedLine,
  RfqSummary,
  RfqVendorRequestRow,
  RfqVendorSuggestion,
  scanVendorContactUs,
  submitVendorContactUs,
  SHAPE_OPTIONS,
  BuyerLineOptions,
  CatalogVendorHit,
  RfqInlineQuote,
  SubmitOutcome,
  createRfq,
  deleteRfq,
  draftCustomerEmail,
  extractRfq,
  fetchRfqBuyerOptions,
  fetchRfqMatchPreview,
  generateRfqQuote,
  listRfqs,
  loadRfq,
  messageOf,
  missingFieldLabel,
  prepareRfqLlmVendor,
  reopenRfq,
  saveRfqVendorEmail,
  selectRfqLineVendor,
  setRfqLineAlsoOffer,
  searchRfqCatalogVendors,
  searchRfqCustomers,
  CustomerCandidate,
  CustomerCandidateSource,
  sendRfqPartialDeadlineDecision,
  setRfqFulfillmentChoice,
  fetchRfqOperatorFlags,
  fetchVendorEmailPreview,
  sendVendorEmails,
  regenerateRfqQuote,
  submitRfq,
  suggestStandard,
  updateRfq,
  VendorOutreachDraft,
} from "./api";
import { FitTable } from "./FitTable";
import { DECLINE_KIND_CLASS, DECLINE_KIND_LABELS } from "./VendorReply";
import { VendorProvenanceChip } from "./VendorProvenance";
import { VendorReplyPanel } from "./VendorReplyPanel";
import { vendorThreadOf } from "./vendorThread";
import { GenerateQuoteCheckDialog } from "./GenerateQuoteCheckDialog";
import { quoteNeedsConfirmation, quoteReadinessOf, type QuoteReadiness } from "./quoteReadiness";
import { BetterPricePanel } from "./BetterPricePanel";
import { CustomerWebsite } from "./CustomerWebsite";
import RfqQuotePanel from "./RfqQuotePanel";
import RfqUnknownItemPanel from "./RfqUnknownItemPanel";
import { loadQuote, shareQuote } from "../quotes/api";
import { CustomerEmailPanel } from "./CustomerEmailPanel";
import { CustomerEmailsPanel } from "./CustomerEmailsPanel";
import { SuperUserPricePanel } from "../components/SuperUser/SuperUserPricePanel";
import { useSuperUserMode } from "../buyerStateDefs";

const MAX_ATTACHMENT_BYTES = 10 * 1024 * 1024;
const ACCEPTED_EXTENSIONS = [
  "pdf", "png", "jpg", "jpeg", "gif", "webp", "heic", "bmp", "tif", "tiff",
  "txt", "md", "rtf", "csv", "tsv", "doc", "docx", "odt", "xls", "xlsx",
  "xlsm", "ods", "numbers", "ppt", "pptx", "odp",
];
const ACCEPT_ATTR = ACCEPTED_EXTENSIONS.map((e) => `.${e}`).join(",");
const PENDING_UPLOAD_KEY = "kono-pending-rfq-upload";

/**
 * Every vendor recommendation says where it came from and when that evidence
 * was last updated, so an operator can weigh "stock confirmed yesterday"
 * against "line card from March" or "AI guess, unverified".
 */
const VENDOR_SOURCE_FALLBACK: Record<string, string> = {
  inventory: "Inventory catalog",
  "line-card": "Verified line card",
  "past-rfq": "Past RFQ reply",
  archive: "California Metals archive",
  llm: "AI suggestion (unverified)",
  catalog: "Vendor catalog (operator pick)",
};

/**
 * Sources whose vendor may not be in the catalog yet: picking one files the
 * record (and drafts the introduction) before the RFQ goes out.
 */
const needsPrepare = (source: RfqVendorSuggestion["source"]): boolean =>
  source === "llm" || source === "archive";

const vendorSourceLabel = (vendor: RfqVendorSuggestion): string =>
  vendor.sourceLabel ||
  VENDOR_SOURCE_FALLBACK[vendor.source || "inventory"] ||
  "Inventory catalog";

const vendorSourceChipClass = (source: RfqVendorSuggestion["source"]): string => {
  if (source === "llm") return "bg-violet-100 text-violet-800";
  if (source === "archive") return "bg-amber-100 text-amber-800";
  if (source === "line-card") return "bg-emerald-100 text-emerald-800";
  if (source === "past-rfq") return "bg-sky-100 text-sky-800";
  if (source === "catalog") return "bg-teal-100 text-teal-800";
  return "bg-gray-100 text-gray-700";
};

const formatSourceDate = (iso: string): string => {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
};

const vendorSourceTitle = (vendor: RfqVendorSuggestion): string => {
  const parts = [vendorSourceLabel(vendor)];
  if (vendor.sourceDetail) parts.push(vendor.sourceDetail);
  if (vendor.sourceUpdatedAt) {
    const d = new Date(vendor.sourceUpdatedAt);
    parts.push(
      `last updated ${Number.isNaN(d.getTime()) ? vendor.sourceUpdatedAt : d.toLocaleString()}`,
    );
  } else {
    parts.push("no update date on record");
  }
  return parts.join(" — ");
};

const websiteHref = (website: string): string =>
  /^https?:\/\//i.test(website) ? website : `https://${website}`;

const emptyAddress = (): Address => ({
  streetLines: ["", ""],
  city: "",
  postalCode: "",
  stateOrProvinceCode: "",
  countryCode: "US",
});

const emptyCustomer: RfqCustomer = {
  name: "",
  company: "",
  email: "",
  phone: "",
  address: emptyAddress(),
};

const isRateableAddress = (address?: Address | null): address is Address =>
  Boolean(
    address &&
      (address.streetLines || []).some((line) => (line || "").trim()) &&
      (address.city || "").trim() &&
      (address.postalCode || "").trim() &&
      (address.countryCode || "").trim(),
  );

const addressOf = (customer: RfqCustomer): Address => ({
  streetLines: [
    customer.address?.streetLines?.[0] || "",
    customer.address?.streetLines?.[1] || "",
  ],
  city: customer.address?.city || "",
  postalCode: customer.address?.postalCode || "",
  stateOrProvinceCode: customer.address?.stateOrProvinceCode || "",
  countryCode: customer.address?.countryCode || "US",
});

const withStreetLine = (
  address: Address,
  index: number,
  value: string,
): Address => {
  const streetLines = [...(address.streetLines || [])];
  while (streetLines.length <= index) streetLines.push("");
  streetLines[index] = value;
  return { ...address, streetLines };
};

/**
 * Blank contact fields filled from the buyer's stored profile. Returns null
 * when nothing was missing, so callers can tell "changed" from "as it was".
 */
const customerWithProfileDefaults = (
  customer: RfqCustomer,
  profile: CustomerProfile,
  authEmail: string | undefined,
): RfqCustomer | null => {
  const next: RfqCustomer = {
    name: customer.name.trim() ? customer.name : profile.name || "",
    company: customer.company.trim()
      ? customer.company
      : profile.companyName || "",
    email: customer.email.trim()
      ? customer.email
      : profile.email || authEmail || "",
    phone: customer.phone.trim()
      ? customer.phone
      : profile.telephone || profile.cellphone || "",
    address: isRateableAddress(customer.address)
      ? customer.address
      : profile.address || customer.address || emptyAddress(),
  };
  const changed =
    next.name !== customer.name ||
    next.company !== customer.company ||
    next.email !== customer.email ||
    next.phone !== customer.phone ||
    JSON.stringify(next.address || null) !==
      JSON.stringify(customer.address || null);
  return changed ? next : null;
};

/** What the form knows and the profile does not — the update worth offering. */
const profileUpdatesFromCustomer = (
  customer: RfqCustomer,
  profile: CustomerProfile,
): CustomerProfile => {
  const updates: CustomerProfile = {};
  const name = customer.name.trim();
  if (name && !(profile.name || "").trim()) updates.name = name;
  const company = customer.company.trim();
  if (company && !(profile.companyName || "").trim()) {
    updates.companyName = company;
  }
  const phone = customer.phone.trim();
  if (
    phone &&
    !(profile.telephone || "").trim() &&
    !(profile.cellphone || "").trim()
  ) {
    updates.telephone = phone;
  }
  if (
    isRateableAddress(customer.address) &&
    !isRateableAddress(profile.address)
  ) {
    updates.address = customer.address;
  }
  return updates;
};

const PROFILE_UPDATE_LABELS: { [key: string]: string } = {
  name: "your name",
  companyName: "your company",
  telephone: "your phone number",
  address: "your ship-to address",
};

const formatProfileValue = (key: string, value: unknown): string => {
  if (key === "address" && value && typeof value === "object") {
    return formatAddress(value as Address, AddressFormat.Full);
  }
  return String(value ?? "");
};

let localLineId = 0;
const newLine = (): RfqLine => ({
  id: `local-${Date.now()}-${localLineId++}`,
  rawText: "",
  material: "",
  standard: "",
  shape: "",
  dimensions: {},
  quantity: "1",
  quantityUnit: "pieces",
  condition: "",
  originCompliance: "",
  additionalSpecifications: "",
  additionalWork: "",
  targetPrice: "",
  missingFields: [],
});

const withDefaultQuantity = (line: RfqLine): RfqLine => ({
  ...line,
  quantity: (line.quantity || "").trim() || "1",
  quantityUnit: (line.quantityUnit || "").trim() || "pieces",
});

/** Client-side mirror of the server's rules, so gaps show before a save. */
/**
 * Which dimensions the form marks as required for a shape, mirroring the
 * server's rules: every listed dimension, except the length of wire and
 * foil (sold by weight or by the roll). Tube-like shapes need the length
 * plus any two of OD / ID / wall, so those three are marked "any two".
 */
const dimensionRequirement = (shape: string, dim: string): "required" | "any-two" | "optional" => {
  const s = shape.trim().toLowerCase();
  if (["round tube", "pipe"].includes(s)) {
    if (dim === "length") return "required";
    if (["outer_diameter", "inner_diameter", "wall_thickness"].includes(dim)) return "any-two";
    return "optional";
  }
  if ((s === "wire" || s === "foil") && dim === "length") return "optional";
  return "required";
};

const clientMissingFields = (line: RfqLine): string[] => {
  const missing: string[] = [];
  if (!line.material.trim()) missing.push("material");
  if (!line.standard.trim()) missing.push("standard");
  if (!line.shape.trim()) missing.push("shape");
  const dims = DIMENSIONS_BY_SHAPE[line.shape.trim().toLowerCase()];
  if (dims) {
    const tubeLike = ["round tube", "pipe"].includes(line.shape.trim().toLowerCase());
    if (tubeLike) {
      if (!(line.dimensions.length || "").trim()) missing.push("dimension:length");
      const present = ["outer_diameter", "inner_diameter", "wall_thickness"].filter(
        (d) => (line.dimensions[d] || "").trim(),
      );
      if (present.length < 2) {
        missing.push(
          "dimension-choice:outer diameter, inner diameter, wall thickness",
        );
      }
    } else {
      for (const dim of dims) {
        // Optional extras (e.g. tube ID when OD+wall given) are handled above;
        // for the plain shapes every listed dimension is required except the
        // ones the server treats as optional.
        const optional =
          (line.shape === "wire" && dim === "length") ||
          (line.shape === "foil" && dim === "length");
        if (!optional && !(line.dimensions[dim] || "").trim()) {
          missing.push(`dimension:${dim}`);
        }
      }
    }
  } else if (line.shape.trim()) {
    if (!Object.values(line.dimensions).some((v) => (v || "").trim())) {
      missing.push("dimension:size");
    }
  }
  if (!line.quantity.trim()) missing.push("quantity");
  return missing;
};

/** The internal portal that matches this site (prod on kono.ai, dev otherwise). */
const internalPortalUrl = (): string => {
  const host = window.location.hostname;
  const api = process.env.REACT_APP_BF_API_BASE_URL || "";
  const prod = host === "kono.ai" || host.endsWith(".kono.ai") || api.includes("gaias-prod");
  return prod ? "https://gaias-internal-prod.web.app" : "https://gaias-internal-dev-2.web.app";
};

const statusBadge = (status: RfqSummary["status"], complete: boolean) => {
  if (status === "quoted") return { text: "Quoted", cls: "bg-green-100 text-green-800" };
  if (status === "submitted")
    return { text: "Choose options", cls: "bg-blue-100 text-blue-800" };
  if (status === "converted") return { text: "Ordered", cls: "bg-green-100 text-green-800" };
  if (status === "waiting-vendors")
    return { text: "Sourcing", cls: "bg-amber-100 text-amber-800" };
  if (status === "closed") return { text: "Closed", cls: "bg-gray-100 text-gray-600" };
  if (complete) return { text: "Ready", cls: "bg-blue-100 text-blue-800" };
  return { text: "Incomplete", cls: "bg-red-50 text-red-700" };
};

/**
 * The partner who answered for a vendor: a reply that came back in our own
 * thread from an address at another company (Aircraft Materials asked,
 * Murphy Nolan answered). Empty when the vendor answered themselves.
 */
const partnerWhoAnswered = (request: RfqVendorRequestRow): string => {
  const from = String(request.responseFrom || "").trim().toLowerCase();
  if (!from.includes("@")) return "";
  const domain = from.split("@")[1] || "";
  const asked = (request.emails || []).map((e) => String(e || "").toLowerCase());
  if (asked.includes(from)) return "";
  if (asked.some((e) => (e.split("@")[1] || "") === domain)) return "";
  return from;
};

const day = (iso: string) => (iso ? iso.slice(0, 10) : "");

const shortDate = (iso: string | null) =>
  iso
    ? new Date(iso).toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
      })
    : "";

const money = (n: number) =>
  n.toLocaleString("en-US", { style: "currency", currency: "USD" });

/** An amount in its own currency: "$12.40" for USD, "CA$12.40" / "EUR 12.40" otherwise. */
const moneyIn = (n: number, currency?: string | null) => {
  const code = (currency || "USD").toUpperCase();
  try {
    return n.toLocaleString("en-US", { style: "currency", currency: code });
  } catch {
    return `${code} ${n.toFixed(2)}`;
  }
};

const leadTimeText = (min: number | null, max: number | null): string => {
  if (min && max && min !== max) return `${min}–${max} days`;
  const single = max || min;
  return single ? `${single} day${single === 1 ? "" : "s"}` : "";
};

const inputCls =
  "w-full rounded-lg border border-gray-300 px-2.5 py-1.5 text-sm text-[#00062e] focus:border-teal-500 focus:outline-none";
const labelCls = "block text-xs font-medium text-gray-500 mb-0.5";

const CUSTOMER_SOURCE_CLASS: Record<CustomerCandidateSource, string> = {
  user: "bg-teal-50 text-teal-800 border-teal-200",
  rfq: "bg-blue-50 text-blue-800 border-blue-200",
  crm: "bg-amber-50 text-amber-800 border-amber-200",
};

const oneLineAddress = (address: Address | null | undefined): string =>
  address
    ? [
        ...(address.streetLines || []),
        address.city,
        address.stateOrProvinceCode,
        address.postalCode,
      ]
        .filter((part) => (part || "").trim())
        .join(", ")
    : "";

type CustomerPick = (candidate: CustomerCandidate, contact?: CustomerCandidate["contacts"][number]) => void;

/**
 * Kono operators typing an RFQ for a customer: as they type in Name, Company
 * or Email, the platform looks for the person or company among kono.ai
 * users, earlier RFQ/quote contacts and CRM accounts (debounced, 2+
 * characters). Picking a row fills the customer block; nothing changes
 * until a row is clicked and every field stays editable afterwards.
 */
function useCustomerLookup(query: string, enabled: boolean) {
  const [hits, setHits] = useState<CustomerCandidate[]>([]);
  const [searching, setSearching] = useState(false);
  const [failed, setFailed] = useState("");
  const [searchedFor, setSearchedFor] = useState("");

  useEffect(() => {
    const q = query.trim();
    if (!enabled || q.length < 2) {
      setHits([]);
      setSearching(false);
      setSearchedFor("");
      return;
    }
    let cancelled = false;
    setSearching(true);
    setFailed("");
    const timer = setTimeout(() => {
      searchRfqCustomers(q)
        .then((found) => {
          if (cancelled) return;
          setHits(found);
          setSearchedFor(q);
        })
        .catch((e) => {
          if (!cancelled) setFailed(messageOf(e));
        })
        .finally(() => {
          if (!cancelled) setSearching(false);
        });
    }, 300);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [query, enabled]);

  return { hits, searching, failed, searchedFor };
}

/** The dropdown under the field being typed in: matches with their source tag, and the account's other people as chips. */
function CustomerSuggestions({
  query,
  hits,
  searching,
  failed,
  onPick,
}: {
  query: string;
  hits: CustomerCandidate[];
  searching: boolean;
  failed: string;
  onPick: CustomerPick;
}) {
  const q = query.trim();
  if (q.length < 2) return null;
  return (
    <div className="absolute z-20 mt-1 max-h-80 w-full min-w-[20rem] overflow-auto rounded-lg border border-gray-300 bg-white text-sm shadow-lg">
      <p className="border-b border-gray-100 px-3 py-1.5 text-[11px] text-gray-500">
        Known customers matching "{q}" - kono.ai users, earlier RFQs and CRM accounts. Pick one to fill the block.
      </p>
      {failed && <p className="px-3 py-2 text-xs text-red-600">{failed}</p>}
      {searching && !hits.length && !failed && (
        <p className="px-3 py-2 text-xs text-gray-500">Searching…</p>
      )}
      {!searching && !hits.length && !failed && (
        <p className="px-3 py-2 text-xs text-gray-500">No match. Keep typing the details by hand.</p>
      )}
      {hits.map((hit) => (
        <div key={hit.key} className="border-b border-gray-100 last:border-b-0">
          <button
            type="button"
            className="flex w-full flex-col items-start gap-0.5 px-3 py-2 text-left hover:bg-gray-50"
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => onPick(hit)}
          >
            <span className="flex w-full flex-wrap items-center gap-2">
              <span className="font-medium text-[#00062e]">{hit.company || hit.name || hit.email}</span>
              {hit.company && hit.name && <span className="text-gray-700">{hit.name}</span>}
              <span className={`ml-auto rounded-full border px-2 py-0.5 text-[10px] font-medium ${CUSTOMER_SOURCE_CLASS[hit.source]}`}>
                {hit.sourceLabel}
              </span>
            </span>
            <span className="text-xs text-gray-600">
              {[hit.email, hit.phone, oneLineAddress(hit.address)].filter(Boolean).join(" · ") || "No contact details on file"}
            </span>
            <span className="text-[11px] text-gray-400">{hit.detail}</span>
          </button>
          {hit.contacts.length > 1 && (
            <div className="flex flex-wrap gap-1 px-3 pb-2">
              {hit.contacts.map((contact, i) => (
                <button
                  key={`${hit.key}:${contact.email || contact.name || i}`}
                  type="button"
                  className="rounded-full border border-gray-300 bg-white px-2 py-0.5 text-[11px] text-gray-700 hover:bg-gray-100"
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => onPick(hit, contact)}
                  title={[contact.title, contact.email, contact.phone].filter(Boolean).join(" · ")}
                >
                  {contact.name || contact.email}
                </button>
              ))}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

/**
 * Everything sharing a quote needs, next to every mention of a generated
 * quote: open it (exactly what the customer sees), copy the link, and share
 * it by email. Sharing an address grants it access — the customer on the
 * quote and kono.ai accounts can always open it; anyone else must be shared
 * first, then signs in with that email to review, purchase, or convert to PO.
 */
function QuoteShareActions({
  quote,
  isKonoTeam,
}: {
  quote: { id: string; reference: string };
  isKonoTeam: boolean;
}) {
  const [copied, setCopied] = useState(false);
  const [sharedWith, setSharedWith] = useState<string[]>([]);
  const [shareInput, setShareInput] = useState("");
  const [sharing, setSharing] = useState(false);
  const [shareNotice, setShareNotice] = useState("");
  const [shareError, setShareError] = useState("");
  const url = `${window.location.origin}/quotes/${quote.id}`;

  useEffect(() => {
    let cancelled = false;
    loadQuote(quote.id)
      .then((q) => {
        if (!cancelled) setSharedWith(q.sharedWithEmails || []);
      })
      .catch(() => {
        /* the share list is a nicety; the link and copy button stand alone */
      });
    return () => {
      cancelled = true;
    };
  }, [quote.id]);

  const share = async () => {
    const emails = shareInput
      .split(/[\s,;]+/)
      .map((e) => e.trim())
      .filter(Boolean);
    if (!emails.length) return;
    const invalid = emails.filter((e) => !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e));
    if (invalid.length) {
      setShareError(`Not an email address: ${invalid.join(", ")}`);
      return;
    }
    setSharing(true);
    setShareError("");
    setShareNotice("");
    try {
      const result = await shareQuote(quote.id, emails);
      setSharedWith(result.sharedWithEmails);
      setShareInput("");
      setShareNotice(
        result.emailed
          ? "Shared — each new address was emailed the link."
          : "Those addresses already had access.",
      );
    } catch (e) {
      setShareError(messageOf(e));
    } finally {
      setSharing(false);
    }
  };

  return (
    <div className="mt-2">
      <div className="flex flex-wrap items-center gap-2">
        <Link
          className="rounded-lg border border-green-300 bg-white px-2.5 py-1 text-xs font-medium text-green-800 hover:bg-green-100"
          to={`/quotes/${quote.id}`}
        >
          Open quote {quote.reference}
        </Link>
        <button
          className="flex items-center gap-1 rounded-lg border border-green-300 bg-white px-2.5 py-1 text-xs font-medium text-green-800 hover:bg-green-100"
          onClick={async () => {
            try {
              await navigator.clipboard.writeText(url);
              setCopied(true);
              setTimeout(() => setCopied(false), 2000);
            } catch {
              /* clipboard denied; the text below is selectable */
            }
          }}
        >
          <ClipboardDocumentIcon className="h-3.5 w-3.5" />
          {copied ? "Link copied" : "Copy quote link"}
        </button>
        <span className="select-all break-all text-xs text-gray-500">{url}</span>
      </div>
      <p className="mt-1.5 text-xs text-gray-600">
        Opening the quote shows exactly what the customer sees — they can pay
        it or convert it to a PO from there.
        {isKonoTeam
          ? " The customer on the quote (and anyone at their company domain) and any kono.ai account can open the link as is; share it with other addresses below — each gets an email whose link lands them checkout-ready."
          : ""}
      </p>
      <div className="mt-2 flex flex-wrap items-center gap-2">
        <input
          className="w-72 rounded-lg border border-gray-300 px-2.5 py-1 text-xs text-[#00062e] focus:border-teal-500 focus:outline-none"
          placeholder="Share by email: name@company.com, colleague@kono.ai"
          value={shareInput}
          onChange={(e) => {
            setShareInput(e.target.value);
            setShareError("");
            setShareNotice("");
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter") void share();
          }}
        />
        <button
          className="rounded-lg bg-[#00062e] px-3 py-1 text-xs font-medium text-white hover:bg-[#001858] disabled:opacity-50"
          onClick={() => void share()}
          disabled={sharing || !shareInput.trim()}
        >
          {sharing ? "Sharing…" : "Share by email"}
        </button>
      </div>
      {shareNotice && (
        <p className="mt-1 text-xs text-green-700">{shareNotice}</p>
      )}
      {shareError && <p className="mt-1 text-xs text-red-600">{shareError}</p>}
      {sharedWith.length > 0 && (
        <p className="mt-1 text-xs text-gray-500">
          Shared with: {sharedWith.join(", ")}
        </p>
      )}
    </div>
  );
}

export default function RfqWorkspace() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { authUser, authIsLoading } = useFirebaseAuthentication();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const outcomeRef = useRef<HTMLDivElement>(null);

  const [saved, setSaved] = useState<RfqSummary[] | null>(null);
  const [error, setError] = useState("");
  const [extracting, setExtracting] = useState(false);
  const [saving, setSaving] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [pastedText, setPastedText] = useState("");

  // The RFQ being edited. `currentId` empty means a not-yet-saved draft.
  const [currentId, setCurrentId] = useState("");
  const [currentStatus, setCurrentStatus] = useState<Rfq["status"]>("draft");
  // Who did what on the open RFQ, shared with coworkers at the same company.
  const [currentActivity, setCurrentActivity] = useState<ActivityEntry[]>([]);
  const [customer, setCustomer] = useState<RfqCustomer>(emptyCustomer);
  const [lines, setLines] = useState<RfqLine[]>([]);
  const [priority, setPriority] = useState<RfqPriority>("price");
  const [neededBy, setNeededBy] = useState("");
  const [deliveryLocation, setDeliveryLocation] = useState("");
  const [notes, setNotes] = useState("");
  const [sourceFile, setSourceFile] = useState<{ filename: string; filetype: string } | null>(null);
  const [sourceText, setSourceText] = useState("");
  const [dirty, setDirty] = useState(false);
  const [outcome, setOutcome] = useState<SubmitOutcome | null>(null);
  const [quoteInfo, setQuoteInfo] = useState<{ id: string; reference: string } | null>(null);
  /** A vendor beat the quote after it was generated (operators). */
  const [betterPrice, setBetterPrice] = useState<BetterPrice | null>(null);
  const [vendorDeadline, setVendorDeadline] = useState("");
  /** Kono team: the facts the internal RFQs list shows, repeated here so the workspace tells the current state. */
  const [statusFacts, setStatusFacts] = useState<{
    assignedTo: string;
    uploadedBy: string;
    closedReason: string;
    createdAt: string;
    updatedAt: string;
  } | null>(null);
  // Submitted without emailing any vendor (operator-selects mode): the
  // operator picks the vendors here and sends.
  const [vendorSelectionPending, setVendorSelectionPending] = useState(false);
  const [draft, setDraft] = useState<{ subject: string; body: string } | null>(null);
  const [draftCopied, setDraftCopied] = useState(false);
  // Kono-internal: per-line inventory options with vendor and provenance.
  const [matches, setMatches] = useState<RfqLineOptions[] | null>(null);
  const [matchesLoading, setMatchesLoading] = useState(false);
  // Kono-internal: the inventory item being edited with super powers from the
  // Sourcing panel. Edits land on the item itself (price, lead time, standard,
  // sizes...), so they reach this RFQ and every later transaction on it.
  const superUserActive = useSuperUserMode((state) => state.active);
  const [editingProductId, setEditingProductId] = useState<string | null>(null);
  // Kono-internal: inline "add email" drafts for vendors without one.
  const [vendorEmailDrafts, setVendorEmailDrafts] = useState<{
    [supplierId: string]: string;
  }>({});
  const [savingVendorEmail, setSavingVendorEmail] = useState("");
  /** Contact-Us crawl results per vendor without an email (Kono team). */
  const [contactUsScans, setContactUsScans] = useState<Record<string, ContactUsScanResult & { values: Record<string, string>; sent?: { ok: boolean; note: string } }>>({});
  const [scanningContactUs, setScanningContactUs] = useState("");
  const [sendingContactUs, setSendingContactUs] = useState("");
  // Kono-internal: per line, the "add a vendor from the catalog" search.
  const [vendorSearch, setVendorSearch] = useState<{
    [lineId: string]: { query: string; hits: CatalogVendorHit[]; searching: boolean };
  }>({});
  const [llmIntros, setLlmIntros] = useState<{
    [supplierId: string]: { subject: string; body: string };
  }>({});
  // Kono-internal: the outreach board — who was asked, who answered.
  const [previewVendorEmails, setPreviewVendorEmails] = useState(true);
  const [disableVendorRfqs, setDisableVendorRfqs] = useState(false);
  const [outreachPreview, setOutreachPreview] = useState<{
    fromEmail: string;
    fromName: string;
    cc?: string | null;
    drafts: Array<VendorOutreachDraft & { sent?: boolean }>;
  } | null>(null);
  const [previewingOutreach, setPreviewingOutreach] = useState(false);
  const [sendingOutreachId, setSendingOutreachId] = useState("");
  const [vendorRequests, setVendorRequests] = useState<
    RfqVendorRequestRow[] | null
  >(null);
  /** Vendor outreach rows whose reply text is unfolded. */
  const [openVendorReplies, setOpenVendorReplies] = useState<Record<string, boolean>>({});
  /** Per line: the operator asked to see the possible vendors behind the clear ones. */
  const [showPossibleVendors, setShowPossibleVendors] = useState<Record<string, boolean>>({});
  /** The vendor request whose decline-reply panel is open. */
  const [replyingTo, setReplyingTo] = useState<string | null>(null);
  const reloadVendorRequests = () => {
    if (!currentId) return;
    void loadRfq(currentId)
      .then((r) => setVendorRequests(r.vendorRequests || null))
      .catch(() => {});
  };
  const [generatingQuote, setGeneratingQuote] = useState(false);
  /** The check shown before a quote is generated: what would be left out, who has not answered. */
  const [quoteCheck, setQuoteCheck] = useState<{ action: "generate" | "send-now" | "regenerate"; readiness: QuoteReadiness } | null>(null);
  /** The last Generate quote that produced nothing, and what it found instead. */
  const [quoteAttempt, setQuoteAttempt] = useState<RfqQuoteAttempt | null>(null);
  const [removedLines, setRemovedLines] = useState<RfqRemovedLine[]>([]);
  const [reopening, setReopening] = useState(false);
  /** The operator's "why this vendor", per line, before it is saved. */
  const [pickReasons, setPickReasons] = useState<Record<string, string>>({});
  const [savingPick, setSavingPick] = useState<string | null>(null);
  const [customerEmailKey, setCustomerEmailKey] = useState(0);
  /** The customer email thread (Kono team), for the last step / next step card. */
  const [emailThread, setEmailThread] = useState<EmailThread | null>(null);
  const [customerEmailState, setCustomerEmailState] = useState<string | null>(null);
  const [buyerOptions, setBuyerOptions] = useState<BuyerLineOptions[] | null>(
    null,
  );
  const [inlineQuote, setInlineQuote] = useState<RfqInlineQuote | null>(null);
  const [quoteFlags, setQuoteFlags] = useState({
    showOptions: false,
    allowAddToCart: true,
    allowSaveQuote: true,
  });
  const [unknownLineIds, setUnknownLineIds] = useState<string[]>([]);
  const [fulfillmentChoice, setFulfillmentChoice] = useState<
    "send-now" | "send-at-deadline" | "hold" | ""
  >("");
  const [savingChoice, setSavingChoice] = useState(false);
  const [partialDeadlineOpen, setPartialDeadlineOpen] = useState(false);
  const [partialCustomBody, setPartialCustomBody] = useState("");
  const [sendingPartialDecision, setSendingPartialDecision] = useState(false);
  // The signed-in buyer's stored profile: prefills the contact form, and
  // after a submit we offer to store back whatever it was missing.
  const [profileDefaults, setProfileDefaults] = useState<CustomerProfile | null>(null);
  const [profilePrompt, setProfilePrompt] = useState<CustomerProfile | null>(null);
  const [profileSaving, setProfileSaving] = useState(false);
  const [profileNote, setProfileNote] = useState("");
  // Extraction can finish before the profile has loaded; this remembers to
  // fill the blanks when it arrives.
  const prefillPendingRef = useRef(false);
  // Mirror for callbacks created before the profile loaded.
  const profileRef = useRef<CustomerProfile | null>(null);
  useEffect(() => {
    profileRef.current = profileDefaults;
  }, [profileDefaults]);

  const isKonoTeam = (authUser?.email || "").toLowerCase().endsWith("@kono.ai");

  // Operator typing the customer: which of Name / Company / Email is being
  // typed in (its text is the lookup), and what was last filled from a pick.
  const [lookupField, setLookupField] = useState<"name" | "company" | "email" | null>(null);
  const [pickedCustomer, setPickedCustomer] = useState<CustomerCandidate | null>(null);
  const lookup = useCustomerLookup(lookupField ? customer[lookupField] : "", isKonoTeam && !!lookupField);
  const applyCustomerPick: CustomerPick = (hit, contact) => {
    const person = contact || { name: hit.name, title: "", email: hit.email, phone: hit.phone };
    setCustomer((prev) => ({
      ...prev,
      name: person.name || prev.name,
      company: hit.company || prev.company,
      email: person.email || prev.email,
      phone: person.phone || hit.phone || prev.phone,
      address: hit.address
        ? {
            ...emptyAddress(),
            ...hit.address,
            streetLines: [hit.address.streetLines?.[0] || "", hit.address.streetLines?.[1] || ""],
            countryCode: hit.address.countryCode || "US",
          }
        : prev.address,
      website: hit.website
        ? { url: hit.website, domain: "", status: "unknown", title: "", summary: "", relevant: null, relevance: "", checkedAt: "" }
        : prev.website,
    }));
    setPickedCustomer(hit);
    setLookupField(null);
    setDirty(true);
  };
  const alreadySubmitted = [
    "quoted",
    "submitted",
    "closed",
    "waiting-vendors",
  ].includes(currentStatus);

  useEffect(() => {
    if (!outcome) return;
    outcomeRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  }, [outcome]);
  // Kono operators can flip the left panel to every customer's RFQs.
  const [listScope, setListScope] = useState<"mine" | "all">(
    searchParams.get("scope") === "all" ? "all" : "mine",
  );
  useEffect(() => {
    const wanted = searchParams.get("scope") === "all" ? "all" : "mine";
    if (searchParams.has("scope")) setListScope(wanted);
  }, [searchParams]);
  const hasWork = lines.length > 0;
  const linesWithMissing = useMemo(
    () => lines.map((l) => ({ ...l, missingFields: clientMissingFields(l) })),
    [lines],
  );
  const missingCount = linesWithMissing.reduce(
    (acc, l) => acc + l.missingFields.length,
    0,
  );
  const complete = hasWork && missingCount === 0;

  const refreshList = useCallback(async () => {
    try {
      setSaved(await listRfqs(listScope));
    } catch (e) {
      setSaved([]);
    }
  }, [listScope]);

  useEffect(() => {
    if (authIsLoading || !authUser) return;
    void refreshList();
  }, [authIsLoading, authUser, refreshList]);

  useEffect(() => {
    if (authIsLoading || !authUser || !isKonoTeam) return;
    let cancelled = false;
    void fetchRfqOperatorFlags()
      .then((flags) => {
        if (!cancelled) {
          setPreviewVendorEmails(flags.previewVendorEmails !== false);
          setDisableVendorRfqs(flags.disableVendorRfqs === true);
        }
      })
      .catch(() => {
        if (!cancelled) setPreviewVendorEmails(true);
      });
    return () => {
      cancelled = true;
    };
  }, [authIsLoading, authUser, isKonoTeam]);

  // Buyers only — for the Kono team the form describes the CUSTOMER, and
  // prefilling it with the operator's own details would be wrong.
  useEffect(() => {
    if (authIsLoading || !authUser || isKonoTeam) return;
    let cancelled = false;
    (async () => {
      try {
        const resp = await sendAuthenticatedRequest(
          `${process.env.REACT_APP_USER_API_BASE_URL}/profile`,
          { method: "GET" },
        );
        if (!resp.ok) return;
        const data = await resp.json();
        if (!cancelled && data?.profile) setProfileDefaults(data.profile);
      } catch {
        /* prefill is best-effort */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [authIsLoading, authUser, isKonoTeam]);

  // The late half of the prefill race: profile arrived after the extraction.
  useEffect(() => {
    if (!profileDefaults || !prefillPendingRef.current) return;
    prefillPendingRef.current = false;
    setCustomer((prev) => {
      const filled = customerWithProfileDefaults(
        prev,
        profileDefaults,
        authUser?.email || undefined,
      );
      return filled || prev;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profileDefaults]);

  const resetEditor = () => {
    setCurrentId("");
    setCurrentStatus("draft");
    setVendorSelectionPending(false);
    setCurrentActivity([]);
    setCustomer(emptyCustomer);
    setLines([]);
    setPriority("price");
    setNeededBy("");
    setDeliveryLocation("");
    setNotes("");
    setSourceFile(null);
    setSourceText("");
    setOutcome(null);
    setQuoteInfo(null);
    setBetterPrice(null);
    setVendorDeadline("");
    setDraft(null);
    setMatches(null);
    setOutreachPreview(null);
    setVendorRequests(null);
    setBuyerOptions(null);
    setInlineQuote(null);
    setQuoteFlags({
      showOptions: false,
      allowAddToCart: true,
      allowSaveQuote: true,
    });
    setUnknownLineIds([]);
    setFulfillmentChoice("");
    setProfilePrompt(null);
    setProfileNote("");
    prefillPendingRef.current = false;
    setDirty(false);
    setError("");
  };

  const openRfq = useCallback(
    async (id: string) => {
      setError("");
      setOutcome(null);
      setDraft(null);
      setMatches(null);
      setOutreachPreview(null);
      setProfilePrompt(null);
      setProfileNote("");
      prefillPendingRef.current = false;
      try {
        const { rfq, vendorRequests: requests } = await loadRfq(id);
        setCurrentId(rfq.id);
        setCurrentStatus(rfq.status);
        setCurrentActivity(rfq.activity || []);
        setCustomer(rfq.customer);
        setLines(rfq.lines.map(withDefaultQuantity));
        setPriority(rfq.priority);
        setNeededBy(rfq.neededBy);
        setDeliveryLocation(rfq.deliveryLocation);
        setNotes(rfq.notes);
        setSourceFile(rfq.sourceFile);
        setSourceText(rfq.sourceText || "");
        setQuoteInfo(
          rfq.quoteId && rfq.quoteReference
            ? { id: rfq.quoteId, reference: rfq.quoteReference }
            : null,
        );
        setBetterPrice(rfq.betterPrice || null);
        setVendorDeadline(rfq.vendorDeadlineAt || "");
        setStatusFacts({
          assignedTo: rfq.assignedTo?.email || "",
          uploadedBy: rfq.mailboxSource
            ? `${rfq.mailboxSource.mailbox} (mailbox)`
            : rfq.ownerIsKonoTeam
              ? rfq.ownerEmail || "Kono operator"
              : "customer on kono.ai",
          closedReason: rfq.closedReason || "",
          createdAt: rfq.createdAt,
          updatedAt: rfq.updatedAt,
        });
        setVendorSelectionPending(!!rfq.vendorSelectionPendingAt);
        // A Generate quote that found nothing stays on screen until the
        // situation is fixed and the quote is generated.
        setQuoteAttempt(rfq.quoteId ? null : rfq.lastQuoteAttempt || null);
        setRemovedLines(rfq.removedLines || []);
        setPickReasons({});
        setVendorRequests(requests || null);
        setUnknownLineIds(rfq.unknownLineIds || []);
        setFulfillmentChoice(rfq.operatorFulfillmentChoice || "");
        setPartialDeadlineOpen(!!rfq.deadlinePartialNotifiedAt);
        setPartialCustomBody(rfq.partialCustomEmailDraft || "");
        setBuyerOptions(null);
        setInlineQuote(null);
        if (
          rfq.quoteId ||
          rfq.status === "quoted" ||
          rfq.status === "submitted"
        ) {
          void fetchRfqBuyerOptions(rfq.id)
            .then((state) => {
              setBuyerOptions(state.options);
              setInlineQuote(state.quote);
              setQuoteFlags({
                showOptions: state.showOptions,
                allowAddToCart: state.allowAddToCart,
                allowSaveQuote: state.allowSaveQuote,
              });
            })
            .catch(() => {});
        }
        setDirty(false);
      } catch (e) {
        setError(messageOf(e));
      }
    },
    [],
  );

  // Deep link: /rfq?id=... (used by our emails and the left panel).
  useEffect(() => {
    const id = searchParams.get("id");
    if (id && authUser && id !== currentId) void openRfq(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, authUser]);

  const runExtraction = useCallback(
    async (params: { filename?: string; filetype?: string; data?: string; text?: string }) => {
      setExtracting(true);
      setError("");
      try {
        const extraction = await extractRfq(params);
        resetEditor();
        // What the document says wins; the buyer's profile fills the blanks.
        const extracted = { ...emptyCustomer, ...extraction.customer };
        const profile = profileRef.current;
        if (profile) {
          setCustomer(
            customerWithProfileDefaults(extracted, profile, undefined) ||
              extracted,
          );
        } else {
          setCustomer(extracted);
          prefillPendingRef.current = true;
        }
        setLines(extraction.lines.map(withDefaultQuantity));
        setNeededBy(extraction.neededBy);
        setDeliveryLocation(extraction.deliveryLocation);
        setNotes(extraction.notes);
        setSourceFile(
          params.filename
            ? { filename: params.filename, filetype: params.filetype || "" }
            : null,
        );
        setSourceText(extraction.sourceText || params.text || "");
        setDirty(true);
        setPastedText("");
      } catch (e) {
        setError(messageOf(e));
      } finally {
        setExtracting(false);
      }
    },
    [],
  );

  // An upload made before sign-in survives the trip through /signin here.
  useEffect(() => {
    if (authIsLoading || !authUser) return;
    try {
      const raw = sessionStorage.getItem(PENDING_UPLOAD_KEY);
      if (!raw) return;
      sessionStorage.removeItem(PENDING_UPLOAD_KEY);
      const pending = JSON.parse(raw);
      if (pending?.data || pending?.text) void runExtraction(pending);
    } catch {
      /* nothing pending */
    }
  }, [authIsLoading, authUser, runExtraction]);

  const handleFile = (file: File | null | undefined) => {
    if (!file) return;
    const ext = (file.name.split(".").pop() || "").toLowerCase();
    if (
      !ACCEPTED_EXTENSIONS.includes(ext) &&
      !file.type.startsWith("image/") &&
      !file.type.startsWith("text/") &&
      file.type !== "application/pdf"
    ) {
      setError(`"${file.name}" is not a supported file type.`);
      return;
    }
    if (file.size > MAX_ATTACHMENT_BYTES) {
      setError("The file is larger than 10 MB. Attach a smaller export.");
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      const params = {
        filename: file.name,
        filetype: file.type,
        data: String(reader.result || ""),
      };
      if (!authUser) {
        try {
          sessionStorage.setItem(PENDING_UPLOAD_KEY, JSON.stringify(params));
        } catch {
          /* too large for sessionStorage; they re-pick after signing in */
        }
        navigate(`/signin?redirect=${encodeURIComponent("/rfq")}`);
        return;
      }
      void runExtraction(params);
    };
    reader.readAsDataURL(file);
  };

  const handlePastedText = () => {
    const text = pastedText.trim();
    if (!text) return;
    if (!authUser) {
      try {
        sessionStorage.setItem(PENDING_UPLOAD_KEY, JSON.stringify({ text }));
      } catch {
        /* re-typed after sign-in */
      }
      navigate(`/signin?redirect=${encodeURIComponent("/rfq")}`);
      return;
    }
    void runExtraction({ text });
  };

  const patchLine = (id: string, patch: Partial<RfqLine>) => {
    setLines((prev) => prev.map((l) => (l.id === id ? { ...l, ...patch } : l)));
    setDirty(true);
  };

  // The standard is required, and most buyers know the alloy and the form
  // but not the spec. Once material and shape are in and the spec box is
  // still empty, ask the server for the distributor's default and fill it
  // in flagged as suggested (the customer sees it was not theirs and can
  // change it). Each material/shape/condition combination is asked once
  // per line, so clearing the box by hand does not bring the same
  // suggestion straight back.
  const standardAskedRef = useRef<{ [key: string]: true }>({});
  const [standardSuggesting, setStandardSuggesting] = useState<{ [lineId: string]: boolean }>({});
  useEffect(() => {
    if (["waiting-vendors", "converted"].includes(currentStatus)) return;
    const timer = window.setTimeout(() => {
      for (const line of lines) {
        const material = line.material.trim();
        const shape = line.shape.trim();
        if (!material || !shape || line.standard.trim()) continue;
        const key = `${line.id}|${material.toLowerCase()}|${shape.toLowerCase()}|${line.condition.trim().toLowerCase()}`;
        if (standardAskedRef.current[key]) continue;
        standardAskedRef.current[key] = true;
        setStandardSuggesting((prev) => ({ ...prev, [line.id]: true }));
        void suggestStandard({ material, shape, condition: line.condition })
          .then((suggestion) => {
            if (!suggestion) return;
            setLines((prev) =>
              prev.map((l) =>
                l.id === line.id && !l.standard.trim()
                  ? { ...l, standard: suggestion.standard, standardSuggested: true }
                  : l,
              ),
            );
            setDirty(true);
          })
          .catch(() => {})
          .finally(() =>
            setStandardSuggesting((prev) => {
              const next = { ...prev };
              delete next[line.id];
              return next;
            }),
          );
      }
    }, 600);
    return () => window.clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lines, currentStatus]);

  const patchDimension = (id: string, dim: string, value: string) => {
    setLines((prev) =>
      prev.map((l) =>
        l.id === id ? { ...l, dimensions: { ...l.dimensions, [dim]: value } } : l,
      ),
    );
    setDirty(true);
  };

  const saveBody = () => ({
    customer,
    priority,
    lines: linesWithMissing,
    neededBy,
    deliveryLocation:
      deliveryLocation.trim() ||
      (isRateableAddress(customer.address)
        ? formatAddress(customer.address, AddressFormat.CityStateZip)
        : ""),
    notes,
    sourceFile,
    sourceText,
  });

  const save = async (): Promise<Rfq | null> => {
    if (!hasWork) {
      setError("Add at least one material line before saving.");
      return null;
    }
    setSaving(true);
    setError("");
    try {
      const rfq = currentId
        ? await updateRfq(currentId, saveBody())
        : await createRfq(saveBody());
      setCurrentId(rfq.id);
      setCurrentStatus(rfq.status);
      setCurrentActivity(rfq.activity || currentActivity);
      setLines(rfq.lines.map(withDefaultQuantity));
      setDirty(false);
      void refreshList();
      return rfq;
    } catch (e) {
      setError(messageOf(e));
      return null;
    } finally {
      setSaving(false);
    }
  };

  const submit = async (
    mode: "quote" | "vendors" = "quote",
    drafts?: Array<{
      supplierId: string;
      emails: string[];
      cc?: string[];
      subject: string;
      body: string;
    }>,
  ): Promise<boolean> => {
    setSubmitting(true);
    setError("");
    // A brand-new RFQ gets its id from save(); keep it where the recovery
    // below can see it if the submit call itself is cut off.
    let submittedId = currentId;
    try {
      // Whatever is on screen is what gets submitted.
      const rfq = await save();
      if (!rfq) return false;
      submittedId = rfq.id;
      const result = await submitRfq(rfq.id, mode, drafts);
      setOutcome(result);
      // Anything they typed that their profile lacks is worth keeping —
      // offered, never stored silently.
      if (!isKonoTeam && profileDefaults) {
        const updates = profileUpdatesFromCustomer(rfq.customer, profileDefaults);
        setProfilePrompt(Object.keys(updates).length ? updates : null);
      }
      if (result.outcome === "quoted" || result.outcome === "choose-options") {
        if (result.quoteId && result.quoteReference) {
          setQuoteInfo({ id: result.quoteId, reference: result.quoteReference });
        }
        if (result.quote) setInlineQuote(result.quote);
        if (result.options) setBuyerOptions(result.options);
        setQuoteFlags({
          showOptions: result.showOptions === true,
          allowAddToCart: result.allowAddToCart !== false,
          allowSaveQuote: result.allowSaveQuote !== false,
        });
        setCurrentStatus(
          result.outcome === "quoted" && result.keepSourcing
            ? rfq.status
            : result.outcome === "quoted"
              ? "quoted"
              : "submitted",
        );
      } else if (result.outcome === "quoted-partial") {
        if (result.quoteId && result.quoteReference) {
          setQuoteInfo({ id: result.quoteId, reference: result.quoteReference });
        }
        if (result.quote) setInlineQuote(result.quote);
        if (result.options) setBuyerOptions(result.options);
        setQuoteFlags({
          showOptions: result.showOptions === true,
          allowAddToCart: result.allowAddToCart !== false,
          allowSaveQuote: result.allowSaveQuote !== false,
        });
        setUnknownLineIds(result.unknownLineIds || []);
        setVendorDeadline(result.deadlineAt || "");
        setCurrentStatus(
          result.unavailableLineIds?.length ? "waiting-vendors" : "quoted",
        );
        if (isKonoTeam) {
          void loadRfq(rfq.id)
            .then((r) => setVendorRequests(r.vendorRequests || null))
            .catch(() => {});
        }
      } else {
        setVendorDeadline(result.deadlineAt || "");
        setCurrentStatus("waiting-vendors");
        setUnknownLineIds(result.unknownLineIds || []);
        if (isKonoTeam) {
          void loadRfq(rfq.id)
            .then((r) => setVendorRequests(r.vendorRequests || null))
            .catch(() => {});
        }
      }
      void refreshList();
      return true;
    } catch (e) {
      // A long submit can outlive the browser's connection (proxies cut it
      // around 60s) while the server finishes anyway. Before showing the
      // error, ask the server what actually happened: if the RFQ went
      // through, show the result instead of a scary gateway page.
      // The server usually needs only a few more seconds past the cut-off,
      // so ask more than once. Operator "Convert to quote" leaves the RFQ in
      // draft (sourcing stays open) and records the quote on it, so a quote
      // id counts as success as much as a submitted status does.
      const recovered = await (async () => {
        const id = submittedId;
        if (!id) return false;
        const settled = (status: string) =>
          ["submitted", "waiting-vendors", "quoted", "converted"].includes(status);
        for (let attempt = 0; attempt < 10; attempt += 1) {
          if (attempt > 0) {
            setError(
              "The connection dropped while the server was still working on " +
                "this request. Checking whether it went through...",
            );
            await new Promise<void>((resolve) => setTimeout(resolve, 6000));
          }
          try {
            const { rfq, vendorRequests: requests } = await loadRfq(id);
            const quoted = Boolean(rfq.quoteId && rfq.quoteReference);
            if (!settled(rfq.status) && !quoted) continue;
            setError("");
            setCurrentStatus(rfq.status);
            setQuoteInfo(
              quoted ? { id: rfq.quoteId!, reference: rfq.quoteReference! } : null,
            );
            if (quoted && mode === "quote") {
              setOutcome({
                outcome: "quoted",
                quoteId: rfq.quoteId,
                quoteReference: rfq.quoteReference,
                keepSourcing: !settled(rfq.status),
              } as SubmitOutcome);
            }
            if (isKonoTeam && requests) setVendorRequests(requests);
            void refreshList();
            return true;
          } catch {
            // Network still unhappy; try again.
          }
        }
        return false;
      })();
      if (!recovered) {
        setError(messageOf(e));
        return false;
      }
      return true;
    } finally {
      setSubmitting(false);
    }
  };

  const openVendorEmailPreview = async () => {
    setPreviewingOutreach(true);
    setError("");
    try {
      const rfq = await save();
      if (!rfq) return;
      const result = await fetchVendorEmailPreview(rfq.id);
      const alreadySent = new Set(
        (vendorRequests || []).map((request) =>
          request.supplierName.trim().toLowerCase(),
        ),
      );
      setOutreachPreview({
        fromEmail: result.fromEmail,
        fromName: result.fromName,
        cc: result.cc || null,
        drafts: result.drafts.map((draft) => ({
          ...draft,
          sent: alreadySent.has(draft.supplierName.trim().toLowerCase()),
        })),
      });
    } catch (e) {
      setError(messageOf(e));
    } finally {
      setPreviewingOutreach(false);
    }
  };

  /** "a@x.com, b@y.com; c@z.com" -> the addresses; typed text stays in the box until blur. */
  const splitAddresses = (text: string): string[] =>
    text
      .split(/[,;\s]+/)
      .map((value) => value.trim())
      .filter(Boolean);

  const patchOutreachDraft = (
    supplierId: string,
    patch: Partial<VendorOutreachDraft>,
  ) => {
    setOutreachPreview((prev) =>
      prev
        ? {
            ...prev,
            drafts: prev.drafts.map((draft) =>
              draft.supplierId === supplierId ? { ...draft, ...patch } : draft,
            ),
          }
        : prev,
    );
  };

  /**
   * Drop a vendor from this round of outreach: the draft leaves the preview,
   * so "Send all remaining" skips them. Nothing is recorded; the vendor is
   * offered again next time the preview opens.
   */
  const removeOutreachDraft = (supplierId: string) => {
    setOutreachPreview((prev) =>
      prev
        ? {
            ...prev,
            drafts: prev.drafts.filter(
              (draft) => draft.supplierId !== supplierId || draft.sent,
            ),
          }
        : prev,
    );
  };

  const sendOutreachDrafts = async (supplierIds: string[]) => {
    if (!outreachPreview) return;
    const drafts = outreachPreview.drafts.filter(
      (draft) => supplierIds.includes(draft.supplierId) && !draft.sent,
    );
    if (!drafts.length) return;
    const payload = drafts.map((draft) => ({
      supplierId: draft.supplierId,
      emails: draft.emails,
      cc: draft.cc || [],
      subject: draft.subject,
      body: draft.body,
    }));
    setSendingOutreachId(supplierIds.length === 1 ? supplierIds[0] : "all");
    setError("");
    try {
      if ((currentStatus === "waiting-vendors" || currentStatus === "quoted") && currentId) {
        await sendVendorEmails(currentId, payload);
        setVendorSelectionPending(false);
        void loadRfq(currentId)
          .then((r) => setVendorRequests(r.vendorRequests || null))
          .catch(() => {});
      } else {
        const ok = await submit("vendors", payload);
        if (!ok) return;
      }
      setOutreachPreview((prev) =>
        prev
          ? {
              ...prev,
              drafts: prev.drafts.map((draft) =>
                supplierIds.includes(draft.supplierId)
                  ? { ...draft, sent: true }
                  : draft,
              ),
            }
          : prev,
      );
    } catch (e) {
      setError(messageOf(e));
    } finally {
      setSendingOutreachId("");
    }
  };

  /** They said yes: store the offered fields on their profile. */
  const saveProfileUpdates = async () => {
    if (!profilePrompt || !profileDefaults) return;
    setProfileSaving(true);
    setProfileNote("");
    try {
      // The whole merged profile, because the endpoint re-checks mandatory
      // fields — sending only the new ones would look like blanking the rest.
      const merged = { ...profileDefaults, ...profilePrompt };
      const resp = await sendAuthenticatedRequest(
        `${process.env.REACT_APP_USER_API_BASE_URL}/profile`,
        { method: "POST", jsonBody: { profile: merged } },
      );
      if (!resp.ok) {
        const data = await resp.json().catch(() => ({} as any));
        throw new Error(
          data?.error ||
            "We could not update your profile — you can edit it under My information.",
        );
      }
      setProfileDefaults(merged);
      setProfilePrompt(null);
      setProfileNote("Your profile has been updated for future RFQs.");
    } catch (e) {
      setProfileNote(messageOf(e));
    } finally {
      setProfileSaving(false);
    }
  };

  /**
   * Kono operator only: quote the customer NOW from what is available —
   * their call on whether to wait for more vendor answers first.
   */
  const chooseFulfillment = async (
    choice: "send-now" | "send-at-deadline" | "hold",
    checked = false,
  ) => {
    if (!currentId) return;
    if (choice === "send-now" && !checked) {
      const readiness = quoteReadinessOf(lines, matches, vendorRequests, vendorDeadline || null);
      if (quoteNeedsConfirmation(readiness)) {
        setQuoteCheck({ action: "send-now", readiness });
        return;
      }
    }
    if (
      choice === "send-now" &&
      !checked &&
      !window.confirm(
        "Generate the quote from what is available right now and email it " +
          "to the customer?",
      )
    ) {
      return;
    }
    setSavingChoice(true);
    setError("");
    try {
      const result = await setRfqFulfillmentChoice(currentId, choice);
      setFulfillmentChoice(choice === "send-now" ? "" : choice);
      if (result.quoteId && result.quoteReference) {
        setQuoteInfo({
          id: result.quoteId,
          reference: result.quoteReference,
        });
        setCurrentStatus(result.status);
      }
      void refreshList();
    } catch (e) {
      setError(messageOf(e));
    } finally {
      setSavingChoice(false);
    }
  };

  const decidePartialDeadline = async (
    action: "send-quote" | "custom-email",
  ) => {
    if (!currentId) return;
    if (action === "custom-email" && !partialCustomBody.trim()) {
      setError("Write the email to the customer first.");
      return;
    }
    setSendingPartialDecision(true);
    setError("");
    try {
      const result = await sendRfqPartialDeadlineDecision(currentId, {
        action,
        body: action === "custom-email" ? partialCustomBody : undefined,
      });
      if (result.quoteId && result.quoteReference) {
        setQuoteInfo({
          id: result.quoteId,
          reference: result.quoteReference,
        });
        setCurrentStatus(result.status);
        setPartialDeadlineOpen(false);
      } else if (result.sent) {
        setPartialDeadlineOpen(false);
      }
      void refreshList();
    } catch (e) {
      setError(messageOf(e));
    } finally {
      setSendingPartialDecision(false);
    }
  };

  const generateQuoteNow = async (checked = false) => {
    if (!currentId) return;
    if (!checked) {
      const readiness = quoteReadinessOf(lines, matches, vendorRequests, vendorDeadline || null);
      if (quoteNeedsConfirmation(readiness)) {
        setQuoteCheck({ action: "generate", readiness });
        return;
      }
    }
    if (
      !checked &&
      !window.confirm(
        "Generate the quote from what is available right now and prepare " +
          "the customer email? Nothing is sent to the customer until you " +
          "review the email and press Send.",
      )
    ) {
      return;
    }
    setGeneratingQuote(true);
    setError("");
    try {
      const result = await generateRfqQuote(currentId, "prepare");
      setCurrentStatus(result.status);
      setQuoteInfo(
        result.quoteId && result.quoteReference
          ? { id: result.quoteId, reference: result.quoteReference }
          : null,
      );
      // Nothing quotable: the RFQ is untouched and still waiting on vendors.
      // Say so plainly and show what the vendors did send.
      setQuoteAttempt(result.quoteId ? null : result.lastQuoteAttempt || null);
      setOutcome(null);
      setCustomerEmailKey((k) => k + 1);
      void refreshList();
    } catch (e) {
      setError(messageOf(e));
    } finally {
      setGeneratingQuote(false);
    }
  };

  /**
   * Operators, after the quote exists: quote again from the picks as they
   * stand (a vendor answered late and was picked, a left-out line can now
   * be covered). The customer's quote becomes the next version when they
   * already saw it; the email is prepared, not sent.
   */
  const regenerateQuoteNow = async (checked = false) => {
    if (!currentId) return;
    if (!checked) {
      const readiness = quoteReadinessOf(lines, matches, vendorRequests, vendorDeadline || null);
      if (quoteNeedsConfirmation(readiness)) {
        setQuoteCheck({ action: "regenerate", readiness });
        return;
      }
      if (
        !window.confirm(
          "Quote this RFQ again from the items picked in Sourcing? If the customer already has the quote, this creates the next version (B, C...) and prepares the email; nothing is sent until you press Send.",
        )
      ) {
        return;
      }
    }
    setGeneratingQuote(true);
    setError("");
    try {
      const result = await regenerateRfqQuote(currentId);
      setCurrentStatus(result.status);
      setQuoteInfo({ id: result.quoteId, reference: result.quoteReference });
      setQuoteAttempt(null);
      setOutcome(null);
      setCustomerEmailKey((k) => k + 1);
      void refreshList();
      void loadRfq(currentId)
        .then((r) => {
          setLines(r.rfq.lines.map(withDefaultQuantity));
          setVendorRequests(r.vendorRequests || null);
        })
        .catch(() => {});
    } catch (e) {
      setError(messageOf(e));
    } finally {
      setGeneratingQuote(false);
    }
  };

  /**
   * Keep the operator's vendor choice and the reason behind it. The reason
   * is what later recommendations quote back ("operators chose them twice
   * for 4130: fastest lead time").
   */
  /** Operators: offer one more option on the quote for a line, or stop. */
  const toggleAlsoOffer = async (lineId: string, productId: string, offer: boolean) => {
    if (!currentId) return;
    setError("");
    try {
      const result = await setRfqLineAlsoOffer(currentId, lineId, { productId, offer });
      patchLine(lineId, { alsoOfferProductIds: result.alsoOfferProductIds });
    } catch (e) {
      setError(messageOf(e));
    }
  };

  const saveVendorPick = async (lineId: string, productId: string) => {
    if (!currentId) return;
    setSavingPick(lineId);
    setError("");
    try {
      await selectRfqLineVendor(currentId, lineId, {
        productId,
        reason: pickReasons[lineId] ?? "",
      });
      patchLine(lineId, {
        preferredProductId: productId,
        preferredProductReason: pickReasons[lineId] ?? "",
      });
    } catch (e) {
      setError(messageOf(e));
    } finally {
      setSavingPick(null);
    }
  };

  /** Put a closed RFQ back in front of the vendors (nothing is emailed). */
  const reopenNow = async () => {
    if (!currentId) return;
    if (
      !window.confirm(
        "Reopen this RFQ? It goes back to waiting on vendors and their " +
          "replies are recognized again. Nothing is sent to anyone.",
      )
    ) {
      return;
    }
    setReopening(true);
    setError("");
    try {
      await reopenRfq(currentId);
      setCurrentStatus("waiting-vendors");
      reloadVendorRequests();
      void refreshList();
    } catch (e) {
      setError(messageOf(e));
    } finally {
      setReopening(false);
    }
  };

  const removeSaved = async (id: string) => {
    if (!window.confirm("Delete this saved RFQ?")) return;
    try {
      await deleteRfq(id);
      if (id === currentId) resetEditor();
      void refreshList();
    } catch (e) {
      setError(messageOf(e));
    }
  };

  /** The sourcing picture for the lines exactly as they stand on screen. */
  const loadMatches = async () => {
    if (!lines.length) return;
    setMatchesLoading(true);
    setError("");
    try {
      setMatches(await fetchRfqMatchPreview(linesWithMissing, priority));
    } catch (e) {
      setError(messageOf(e));
    } finally {
      setMatchesLoading(false);
    }
  };

  // Kono team: the panel loads itself as soon as material lines exist —
  // right after an extraction or when a saved RFQ is opened.
  useEffect(() => {
    if (!isKonoTeam || !lines.length || matches !== null || matchesLoading) {
      return;
    }
    void loadMatches();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isKonoTeam, lines.length, matches, matchesLoading]);

  /**
   * File an email for a vendor that has none: the vendor becomes checkable
   * everywhere it appears, and is checked for the line it was filed from.
   */
  const addVendorEmail = async (
    lineId: string,
    suggestions: RfqVendorSuggestion[],
    verdict: RfqLineOptions["verdict"],
    supplierId: string,
  ) => {
    const email = (
      vendorEmailDrafts[supplierId] ||
      suggestions.find((v) => v.supplierId === supplierId)?.email ||
      ""
    ).trim();
    if (!email) return;
    const vendor = suggestions.find((v) => v.supplierId === supplierId);
    const line = lines.find((l) => l.id === lineId);
    setSavingVendorEmail(supplierId);
    setError("");
    try {
      if (vendor && needsPrepare(vendor.source) && line) {
        const result = await prepareRfqLlmVendor({
          supplierId,
          name: vendor.name,
          email,
          website: vendor.website,
          phone: vendor.phone,
          line,
          rfqId: currentId || undefined,
        });
        setLlmIntros((prev) => ({ ...prev, [supplierId]: result.draft }));
      } else {
        await saveRfqVendorEmail(supplierId, email, {
          name: vendor?.name,
          website: vendor?.website,
          phone: vendor?.phone,
        });
      }
      setMatches((prev) =>
        prev
          ? prev.map((entry) => ({
              ...entry,
              vendorSuggestions: entry.vendorSuggestions.map((v) =>
                v.supplierId === supplierId
                  ? { ...v, hasEmail: true, email }
                  : v,
              ),
            }))
          : prev,
      );
      // They filed the email in order to ask this vendor: check it.
      toggleVendor(lineId, suggestions, verdict, supplierId);
    } catch (e) {
      setError(messageOf(e));
    } finally {
      setSavingVendorEmail("");
    }
  };

  /**
   * A vendor with a website and no email: read the site. An address found on
   * the Contact page is saved on the vendor and dropped into the email box;
   * otherwise the contact form comes back filled with the outreach identity
   * for the operator to read and send.
   */
  const scanContactUs = async (lineId: string, vendor: RfqVendorSuggestion) => {
    setScanningContactUs(vendor.supplierId);
    setError("");
    try {
      const line = lines.find((l) => l.id === lineId);
      const result = await scanVendorContactUs({
        supplierId: vendor.supplierId,
        name: vendor.name,
        website: vendor.website,
        materials: line ? ([line.material, line.standard].filter(Boolean) as string[]) : [],
      });
      setContactUsScans((prev) => ({ ...prev, [vendor.supplierId]: { ...result, values: result.form?.values || {} } }));
      if (result.savedEmail) {
        setMatches((prev) =>
          prev
            ? prev.map((entry) => ({
                ...entry,
                vendorSuggestions: entry.vendorSuggestions.map((v) => (v.supplierId === vendor.supplierId ? { ...v, hasEmail: true, email: result.savedEmail } : v)),
              }))
            : prev,
        );
      } else if (result.scan.emails[0]) {
        setVendorEmailDrafts((prev) => ({ ...prev, [vendor.supplierId]: result.scan.emails[0] }));
      }
    } catch (e) {
      setError(messageOf(e));
    } finally {
      setScanningContactUs("");
    }
  };

  const sendContactUs = async (vendor: RfqVendorSuggestion) => {
    const entry = contactUsScans[vendor.supplierId];
    if (!entry?.scan.form) return;
    if (!window.confirm(`Send the Contact Us form on ${entry.scan.contactUrl || entry.scan.website} as ${entry.identity.name} (${entry.identity.email})?`)) return;
    setSendingContactUs(vendor.supplierId);
    setError("");
    try {
      const result = await submitVendorContactUs({
        supplierId: entry.savedSupplierId || vendor.supplierId,
        website: entry.scan.website,
        contactUrl: entry.scan.contactUrl,
        form: entry.scan.form,
        values: entry.values,
      });
      setContactUsScans((prev) => ({ ...prev, [vendor.supplierId]: { ...prev[vendor.supplierId], sent: { ok: result.ok, note: result.note } } }));
    } catch (e) {
      setError(messageOf(e));
    } finally {
      setSendingContactUs("");
    }
  };

  const prepareLlmVendorIfNeeded = async (
    line: RfqLine | undefined,
    vendor: RfqVendorSuggestion,
  ) => {
    if (!needsPrepare(vendor.source) || !line) return;
    setSavingVendorEmail(vendor.supplierId);
    setError("");
    try {
      const result = await prepareRfqLlmVendor({
        supplierId: vendor.supplierId,
        name: vendor.name,
        email: vendor.email || vendorEmailDrafts[vendor.supplierId],
        website: vendor.website,
        phone: vendor.phone,
        line,
      });
      setLlmIntros((prev) => ({
        ...prev,
        [vendor.supplierId]: result.draft,
      }));
      if (result.hasEmail) {
        setMatches((prev) =>
          prev
            ? prev.map((entry) => ({
                ...entry,
                vendorSuggestions: entry.vendorSuggestions.map((v) =>
                  v.supplierId === vendor.supplierId
                    ? { ...v, hasEmail: true }
                    : v,
                ),
              }))
            : prev,
        );
      }
    } catch (e) {
      setError(messageOf(e));
    } finally {
      setSavingVendorEmail("");
    }
  };

  /**
   * Toggle a vendor for a line. The first touch materializes the default
   * (suggested) set so what you see checked is exactly what gets emailed.
   */
  const toggleVendor = (
    lineId: string,
    suggestions: RfqVendorSuggestion[],
    verdict: RfqLineOptions["verdict"],
    supplierId: string,
  ) => {
    setLines((prev) =>
      prev.map((l) => {
        if (l.id !== lineId) return l;
        const current = Array.isArray(l.vendorIdsToContact)
          ? l.vendorIdsToContact
          : verdict === "unavailable"
            ? suggestions.filter((v) => v.suggested).map((v) => v.supplierId)
            : [];
        const next = current.includes(supplierId)
          ? current.filter((id) => id !== supplierId)
          : [...current, supplierId];
        return { ...l, vendorIdsToContact: next };
      }),
    );
    setDirty(true);
  };

  /** Search the vendor catalog by name for one line's "add a vendor" box. */
  const searchCatalogVendors = async (lineId: string, query: string) => {
    setVendorSearch((prev) => ({
      ...prev,
      [lineId]: { query, hits: prev[lineId]?.hits || [], searching: query.trim().length >= 2 },
    }));
    if (query.trim().length < 2) return;
    try {
      const hits = await searchRfqCatalogVendors(query.trim());
      setVendorSearch((prev) =>
        // A slower answer to an older query must not overwrite the newer one.
        prev[lineId]?.query === query
          ? { ...prev, [lineId]: { query, hits, searching: false } }
          : prev,
      );
    } catch (e) {
      setError(messageOf(e));
      setVendorSearch((prev) => ({
        ...prev,
        [lineId]: { query, hits: [], searching: false },
      }));
    }
  };

  /**
   * Put a catalog vendor the matching did not propose on a line's list and
   * check it. The list entry is local until the next match reload; the
   * server lists operator picks from vendorIdsToContact from then on.
   */
  const addCatalogVendor = (
    lineId: string,
    suggestions: RfqVendorSuggestion[],
    verdict: RfqLineOptions["verdict"],
    hit: CatalogVendorHit,
  ) => {
    const suggestion: RfqVendorSuggestion = {
      supplierId: hit.supplierId,
      name: hit.name,
      hasEmail: hit.hasEmail,
      reasons: ["Added from the vendor catalog by you"],
      suggested: false,
      source: "catalog",
      sourceLabel: "Vendor catalog (operator pick)",
      sourceDetail: "picked by name from the catalog",
      sourceUpdatedAt: null,
      email: hit.email,
      website: hit.website,
      phone: hit.phone,
      country: hit.country || "",
      provenance: "website",
      provenanceLabel: "Picked from the vendor catalog; no match evidence for this line",
    };
    setMatches((prev) =>
      prev
        ? prev.map((entry) =>
            entry.lineId !== lineId ||
            entry.vendorSuggestions.some((v) => v.supplierId === hit.supplierId)
              ? entry
              : { ...entry, vendorSuggestions: [...entry.vendorSuggestions, suggestion] },
          )
        : prev,
    );
    setVendorSearch((prev) => ({ ...prev, [lineId]: { query: "", hits: [], searching: false } }));
    const alreadyChecked = Array.isArray(lines.find((l) => l.id === lineId)?.vendorIdsToContact)
      ? lines.find((l) => l.id === lineId)!.vendorIdsToContact!.includes(hit.supplierId)
      : false;
    if (hit.hasEmail && !alreadyChecked) {
      toggleVendor(lineId, [...suggestions, suggestion], verdict, hit.supplierId);
    }
  };

  const showCustomerDraft = async () => {
    setDraftCopied(false);
    try {
      const rfq = await save();
      if (!rfq) return;
      setDraft(await draftCustomerEmail(rfq.id));
    } catch (e) {
      setError(messageOf(e));
    }
  };

  const copyDraft = async () => {
    if (!draft) return;
    try {
      await navigator.clipboard.writeText(
        `Subject: ${draft.subject}\n\n${draft.body}`,
      );
      setDraftCopied(true);
    } catch {
      /* clipboard denied; the text is selectable */
    }
  };

  const editingLocked = ["waiting-vendors", "converted"].includes(currentStatus);
  // Vendor selection stays open for operators while vendors are being asked:
  // in "operator picks the vendors" mode nothing was emailed on submit, so
  // this is where they tick the vendors and send. Customers cannot change it.
  const vendorPickLocked = currentStatus === "converted" || (editingLocked && !isKonoTeam);
  // Same rule for the replacement pick in Sourcing and "also offer": operators
  // choose the item that fills a line while the vendors are being asked (that
  // is when the vendors' quotes come in); customers cannot touch it then.
  const replacementPickLocked = vendorPickLocked;

  const deadlineText = vendorDeadline
    ? new Date(vendorDeadline).toLocaleString("en-US", {
        weekday: "long",
        month: "short",
        day: "numeric",
        hour: "numeric",
        minute: "2-digit",
      })
    : "";

  // ------------------------------------------------------------------ render

  if (!authIsLoading && !authUser) {
    return (
      <div className="mx-auto mt-12 mb-16 max-w-xl px-4 text-center">
        <h1 className="text-xl font-semibold text-[#00062e]">Upload your RFQ</h1>
        <p className="mt-2 text-sm text-gray-600">
          Attach your RFQ — PDF, Excel, photo, Word, PowerPoint — and we read
          it into a quote request for you. Sign in first so your RFQs are saved
          to your account; you will come right back here.
        </p>
        <div className="mt-5 flex justify-center gap-3">
          <Link
            to={`/signin?redirect=${encodeURIComponent("/rfq")}`}
            className="rounded-lg bg-[#00062e] px-5 py-2 text-sm font-medium text-white hover:bg-[#001858]"
          >
            Sign in
          </Link>
          <Link
            to={`/signup?redirect=${encodeURIComponent("/rfq")}`}
            className="rounded-lg border border-gray-300 px-5 py-2 text-sm font-medium text-[#00062e] hover:bg-gray-50"
          >
            Create an account
          </Link>
        </div>
        {error && <p className="mt-4 text-sm text-red-600">{error}</p>}
      </div>
    );
  }

  /**
   * One row per vendor asked: status, dates, reply text (click to read),
   * reply / follow-up buttons. Shown on the Sourcing board while vendors are
   * answering and under "Vendor replies" once the RFQ is quoted or closed.
   */
  const vendorRequestList = (
    <>
      {vendorRequests === null ? (
        <p className="mt-3 text-sm text-gray-500">
          Loading vendor requests…
        </p>
      ) : vendorRequests.length === 0 ? (
        vendorSelectionPending ? (
          <p className="mt-3 rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-900">
            No vendor has been asked yet. Tick the vendors under
            "Vendors to RFQ" on each item below, then press "Preview
            RFQs to Vendors" to send.
          </p>
        ) : (
          <p className="mt-3 text-sm text-gray-500">
            No vendor could be emailed for the missing lines — check the
            sourcing panel for vendors lacking an email address.
          </p>
        )
      ) : (
        <div className="mt-3 flex flex-col gap-2">
          {vendorRequests.map((r) => {
            const better = betterPrice && betterPrice.status === "open" ? betterPrice.vendors.find((v) => v.requestId === r.id) : null;
            const betterLines = better ? betterPrice!.lines.filter((l) => l.newSupplierId === better.supplierId) : [];
            const betterKinds = [betterLines.some((l) => l.kind === "cheaper") ? "lower price" : "", betterLines.some((l) => l.kind === "now-available") ? "item not quoted before" : ""].filter(Boolean);
            return (
            <div
              key={r.id}
              className={`flex cursor-pointer flex-wrap items-center gap-x-3 gap-y-1 rounded-lg border px-3 py-2 text-sm ${better ? "border-red-400 bg-red-50 hover:bg-red-100/70" : "border-gray-200 bg-white hover:bg-gray-50"}`}
              title={better ? `${r.supplierName} answered with ${betterKinds.join(" and ") || "a better option"} after the quote: the customer may want a new quote. See "Lower price option".` : "Click to read the emails with this vendor"}
              onClick={() =>
                setOpenVendorReplies((prev) => ({ ...prev, [r.id]: !prev[r.id] }))
              }
            >
              <span className="font-medium text-gray-900">
                {r.supplierName}
              </span>
              {better && (
                <span className="rounded bg-red-600 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-white">
                  {betterKinds.join(" + ") || "better option"}
                </span>
              )}
              {r.contactFrom && (
                <span className="rounded-full bg-sky-100 px-1.5 py-0.5 text-[0.65rem] font-medium text-sky-800" title="This record has no email of its own; the RFQ went to the contacts of the linked record of the same vendor.">
                  contact from {r.contactFrom}
                </span>
              )}
              <span
                className={`rounded px-1.5 py-0.5 text-xs font-medium ${
                  r.status === "responded"
                    ? "bg-green-100 text-green-800"
                    : r.status === "declined"
                      ? "bg-red-50 text-red-700"
                      : r.status === "closed"
                        ? "bg-gray-100 text-gray-600"
                        : "bg-amber-100 text-amber-800"
                }`}
              >
                {r.status === "sent"
                  ? `waiting${
                      r.followUpsSent
                        ? ` · ${r.followUpsSent} follow-up${
                            r.followUpsSent === 1 ? "" : "s"
                          }`
                        : ""
                    }`
                  : r.status}
              </span>
              <span className="text-xs text-gray-500">
                {r.emails.join(", ")}
              </span>
              {(() => {
                const last = r.lastEmailAt || r.sentAt;
                const manual = (r.manualFollowUps || []).length;
                const title = [
                  r.sentAt ? `Request sent ${new Date(r.sentAt).toLocaleString()}` : "",
                  r.followUpsSent ? `${r.followUpsSent} follow-up${r.followUpsSent === 1 ? "" : "s"} sent${manual ? ` (${manual} by hand)` : ""}` : "",
                  r.status === "sent" && r.nextFollowUpAt ? `next scheduled follow-up ${new Date(r.nextFollowUpAt).toLocaleString()}` : "",
                ]
                  .filter(Boolean)
                  .join("; ");
                return last ? (
                  <span className="text-xs text-gray-500" title={title || undefined}>
                    {r.followUpsSent ? "last email sent" : "sent"} {new Date(last).toLocaleString()}
                  </span>
                ) : r.queuedUntil ? (
                  <span className="text-xs text-gray-500">queued until {new Date(r.queuedUntil).toLocaleString()}</span>
                ) : null;
              })()}
              {r.status === "declined" && r.declineKind && (
                <span
                  className={`rounded px-1.5 py-0.5 text-xs font-medium ${
                    DECLINE_KIND_CLASS[r.declineKind] || DECLINE_KIND_CLASS.other
                  }`}
                >
                  {DECLINE_KIND_LABELS[r.declineKind] || r.declineKind}
                </span>
              )}
              {(r.declineReason || r.responseSummary) && (
                <span className="text-xs text-gray-600">
                  {r.status === "declined" && r.declineReason
                    ? r.declineReason
                    : r.responseSummary}
                </span>
              )}
              {(r.responseReceivedAt || r.respondedAt) && (
                <span
                  className="text-xs text-gray-500"
                  title={
                    r.responseReceivedAt && r.respondedAt
                      ? `Vendor's email dated ${new Date(r.responseReceivedAt).toLocaleString()}; read by the system ${new Date(r.respondedAt).toLocaleString()}`
                      : undefined
                  }
                >
                  received {new Date(r.responseReceivedAt || r.respondedAt || "").toLocaleString()}
                </span>
              )}
              {partnerWhoAnswered(r) && (
                <span
                  className="rounded bg-violet-100 px-1.5 py-0.5 text-xs font-medium text-violet-800"
                  title={`Answered from ${r.responseFrom}, in the thread of the email we sent to ${r.emails.join(", ")} — their partner replying for them.`}
                >
                  via partner {partnerWhoAnswered(r)}
                </span>
              )}
              {(() => {
                const n = vendorThreadOf(r).length;
                return n > 0 ? (
                  <span className="text-xs text-blue-700">
                    {openVendorReplies[r.id] ? "hide emails" : `show ${n} email${n === 1 ? "" : "s"}`}
                  </span>
                ) : null;
              })()}
              {(r.status === "declined" || r.status === "responded") && isKonoTeam && (() => {
                const previous = r.vendorReply || r.declineReply;
                const thanks = r.status === "responded";
                return (
                  <button
                    type="button"
                    className={`rounded border px-2 py-0.5 text-xs font-medium ${
                      previous
                        ? "border-gray-300 bg-white text-gray-600 hover:bg-gray-100"
                        : "border-blue-300 bg-blue-50 text-blue-800 hover:bg-blue-100"
                    }`}
                    title={
                      previous
                        ? `Replied ${new Date(previous.at).toLocaleString()} by ${previous.by}`
                        : thanks
                          ? "Thank the vendor for their quote; the proposed text is ready to edit"
                          : "Ask the vendor why they declined: not their alloy, not this size, not in stock, coming soon?"
                    }
                    onClick={(e) => {
                      e.stopPropagation();
                      setReplyingTo((cur) => (cur === r.id ? null : r.id));
                    }}
                  >
                    {replyingTo === r.id ? "Close the reply" : previous ? "Replied, reply again" : thanks ? "Reply: thank you" : "Reply: ask why"}
                  </button>
                );
              })()}
              {r.status === "sent" && isKonoTeam && r.sentAt && (
                <button
                  type="button"
                  className="rounded border border-blue-300 bg-blue-50 px-2 py-0.5 text-xs font-medium text-blue-800 hover:bg-blue-100"
                  title={`Chase ${r.supplierName} now, in the same thread, with the text the scheduler would send; the proposal is ready to edit.${
                    r.nextFollowUpAt ? ` The scheduler planned the next one for ${new Date(r.nextFollowUpAt).toLocaleString()}.` : ""
                  }`}
                  onClick={(e) => {
                    e.stopPropagation();
                    setReplyingTo((cur) => (cur === r.id ? null : r.id));
                  }}
                >
                  {replyingTo === r.id ? "Close the follow-up" : r.followUpsSent ? "Follow up again" : "Follow up"}
                </button>
              )}
              {openVendorReplies[r.id] && (
                <div className="mt-1 flex w-full flex-col gap-1.5" onClick={(e) => e.stopPropagation()}>
                  {vendorThreadOf(r).map((m, i) => (
                    <div
                      key={`${m.at}-${i}`}
                      className={`rounded border p-2 text-xs ${m.direction === "in" ? "border-green-200 bg-green-50/60" : "border-gray-200 bg-gray-50"}`}
                    >
                      <div className="flex flex-wrap items-center gap-x-2 text-gray-600">
                        <span className={`rounded px-1.5 py-0.5 font-medium ${m.direction === "in" ? "bg-green-100 text-green-800" : "bg-indigo-100 text-indigo-800"}`}>
                          {m.direction === "in" ? "Vendor" : m.kind === "follow-up" ? "Kono follow-up" : m.kind === "our-reply" ? "Kono reply" : "Kono RFQ"}
                        </span>
                        {m.at && <span>{new Date(m.at).toLocaleString()}</span>}
                        {m.from && <span>from {m.from}</span>}
                        {m.direction === "out" && m.to.length > 0 && <span>to {m.to.join(", ")}</span>}
                        {m.direction === "out" && m.by && m.by !== "schedule" && <span>by {m.by}</span>}
                      </div>
                      {m.subject && <div className="mt-0.5 font-medium text-gray-900">{m.subject}</div>}
                      {m.text ? (
                        <div className="mt-1 max-h-64 overflow-auto whitespace-pre-wrap text-gray-800">{m.text}</div>
                      ) : (
                        <div className="mt-1 italic text-gray-500">Body not kept (sent before the email log existed); the subject and time are the record.</div>
                      )}
                    </div>
                  ))}
                </div>
              )}
              {replyingTo === r.id && (
                <VendorReplyPanel
                  rfqId={currentId}
                  request={r}
                  mode={r.status === "sent" ? "follow-up" : "reply"}
                  onSent={reloadVendorRequests}
                  onClose={() => setReplyingTo(null)}
                />
              )}
            </div>
            );
          })}
        </div>
      )}
    </>
  );

  return (
    <div className="mx-auto mt-6 mb-16 max-w-6xl px-4">
      <div className="flex flex-col gap-6 lg:flex-row">
        {/* ------------------------------------------------ saved RFQs panel */}
        <aside className="w-full shrink-0 lg:w-64">
          <div className="flex items-center justify-between">
            <h2 className="text-sm font-semibold text-[#00062e]">Saved RFQs</h2>
            <button
              onClick={() => {
                resetEditor();
                setSearchParams({});
              }}
              className="inline-flex items-center gap-1 text-xs font-medium text-teal-700 hover:underline"
            >
              <PlusIcon className="h-3.5 w-3.5" /> New RFQ
            </button>
          </div>
          {isKonoTeam && (
            <div className="mt-2 flex overflow-hidden rounded-lg border border-gray-200 text-xs">
              {(
                [
                  ["mine", "Mine"],
                  ["all", "All customers"],
                ] as ["mine" | "all", string][]
              ).map(([value, label]) => (
                <button
                  key={value}
                  onClick={() => {
                    setListScope(value);
                    setSaved(null);
                  }}
                  className={`flex-1 px-2 py-1.5 font-medium ${
                    listScope === value
                      ? "bg-[#00062e] text-white"
                      : "bg-white text-gray-600 hover:bg-gray-50"
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>
          )}
          <div className="mt-2 flex flex-col gap-2">
            {saved === null && (
              <p className="text-xs text-gray-500">Loading...</p>
            )}
            {saved !== null && saved.length === 0 && (
              <p className="text-xs text-gray-500">
                {listScope === "all"
                  ? "No customer RFQs yet."
                  : "Nothing saved yet. Upload an RFQ and save it — finished or not."}
              </p>
            )}
            {(saved || []).map((s) => {
              const badge = statusBadge(s.status, s.complete);
              return (
                <div
                  key={s.id}
                  className={`group cursor-pointer rounded-lg border p-2.5 ${
                    s.id === currentId
                      ? "border-teal-500 bg-teal-50/50"
                      : "border-gray-200 hover:border-gray-300"
                  }`}
                  onClick={() => {
                    setSearchParams({ id: s.id });
                    void openRfq(s.id);
                  }}
                >
                  <div className="flex items-start justify-between gap-1">
                    <div className="min-w-0">
                      <p className="truncate text-sm font-medium text-[#00062e]">
                        {s.firstMaterial}
                      </p>
                      <p className="mt-0.5 text-[0.65rem] text-gray-500">
                        {day(s.createdAt)} · {s.lineCount} item
                        {s.lineCount === 1 ? "" : "s"}
                      </p>
                      {isKonoTeam && s.betterPrice && (
                        <p
                          className="mt-0.5 inline-block rounded bg-amber-100 px-1.5 py-0.5 text-[0.65rem] font-medium text-amber-900"
                          title={`${s.betterPrice.vendors.join(", ")} answered after ${s.betterPrice.quoteReference}: ${s.betterPrice.lines} line(s) improved${s.betterPrice.nowAvailable ? `, ${s.betterPrice.nowAvailable} now available` : ""}`}
                        >
                          Lower price option
                          {s.betterPrice.savings > 0 ? ` · ${moneyIn(s.betterPrice.savings, s.betterPrice.currency)} less` : s.betterPrice.nowAvailable ? " · item now available" : ""}
                        </p>
                      )}
                      {listScope === "all" && (
                        <p className="mt-0.5 truncate text-[0.65rem] text-gray-500">
                          {s.customerEmail || s.ownerEmail}
                          {s.customerCompany ? ` · ${s.customerCompany}` : ""}
                        </p>
                      )}
                      {listScope !== "all" && s.byCoworker && (
                        <p className="mt-0.5 truncate text-[0.65rem] text-gray-500">
                          By {s.createdBy ? actorLabel(s.createdBy, authUser?.email) : s.ownerEmail || "a coworker"}
                        </p>
                      )}
                      {listScope !== "all" && s.lastActivity && (
                        <p className="mt-0.5 truncate text-[0.65rem] text-gray-500">
                          {activityLine(s.lastActivity, authUser?.email)}
                        </p>
                      )}
                    </div>
                    {/* "All customers" only renders for kono.ai accounts, and
                        the backend lets kono staff delete any RFQ — so the
                        trash shows on both tabs, letting operators clean up
                        customers' stale requests too. */}
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        void removeSaved(s.id);
                      }}
                      className="rounded p-1 text-gray-300 hover:text-red-600 group-hover:text-gray-400"
                      aria-label="Delete RFQ"
                    >
                      <TrashIcon className="h-4 w-4" />
                    </button>
                  </div>
                  <span
                    className={`mt-1 inline-block rounded-full px-2 py-0.5 text-[0.6rem] font-medium ${badge.cls}`}
                  >
                    {badge.text}
                  </span>
                </div>
              );
            })}
          </div>
        </aside>

        {/* ------------------------------------------------------ main area */}
        <main className="min-w-0 flex-1">
          <h1 className="text-xl font-semibold text-[#00062e]">
            {currentId ? "Your RFQ" : "Upload an RFQ"}
          </h1>
          {currentId && currentActivity.length > 0 && (
            <ActivityTrail
              className="mt-1"
              activity={currentActivity}
              viewerEmail={authUser?.email}
            />
          )}

          {!hasWork && (
            <>
              <p className="mt-1 max-w-2xl text-sm text-gray-600">
                Attach the request the way you already have it — a PDF, an
                Excel sheet, a photo or screenshot, Word, PowerPoint — and we
                read out every material, size and requirement. You review it,
                fill anything the document did not say, and submit.
              </p>
              <input
                ref={fileInputRef}
                type="file"
                accept={ACCEPT_ATTR}
                className="hidden"
                onChange={(e) => {
                  handleFile(e.target.files?.[0]);
                  e.target.value = "";
                }}
              />
              <div
                onDragOver={(e) => e.preventDefault()}
                onDrop={(e) => {
                  e.preventDefault();
                  handleFile(e.dataTransfer.files?.[0]);
                }}
                className="mt-4 flex cursor-pointer flex-col items-center justify-center rounded-xl border-2 border-dashed border-gray-300 px-6 py-10 text-center hover:border-teal-400"
                onClick={() => fileInputRef.current?.click()}
              >
                <ArrowUpTrayIcon className="h-8 w-8 text-gray-400" />
                <p className="mt-2 text-sm font-medium text-[#00062e]">
                  {extracting
                    ? "Reading your document..."
                    : "Drop your RFQ here, or click to choose a file"}
                </p>
                <p className="mt-1 text-xs text-gray-500">
                  {extracting
                    ? "Large Excel sheets can take a minute. Stay on this page."
                    : "PDF, image, Excel, Word, PowerPoint, text — up to 10 MB"}
                </p>
              </div>
              <div className="mt-4">
                <label className={labelCls}>Or paste the request as text</label>
                <textarea
                  value={pastedText}
                  onChange={(e) => setPastedText(e.target.value)}
                  rows={4}
                  placeholder={'e.g. "Need 6 pcs Inconel 718 round bar, 2.5 in dia x 72 in, AMS 5662, DFARS."'}
                  className={inputCls}
                />
                <button
                  onClick={handlePastedText}
                  disabled={extracting || !pastedText.trim()}
                  className="mt-2 rounded-lg bg-[#00062e] px-4 py-2 text-sm font-medium text-white hover:bg-[#001858] disabled:opacity-50"
                >
                  {extracting ? "Reading..." : "Extract from text"}
                </button>
              </div>
            </>
          )}

          {error && (
            <p className="mt-3 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
              {error}
            </p>
          )}

          {/* ------------------------------------------------ submit outcome */}
          <div ref={outcomeRef}>
          {outcome?.outcome === "choose-options" && (
            <div className="mt-4 flex items-start gap-2 rounded-xl border border-blue-200 bg-blue-50 p-4">
              <CheckCircleIcon className="h-5 w-5 shrink-0 text-blue-600" />
              <p className="text-sm text-gray-800">
                Everything in your RFQ is available. Choose the option you
                want for each item below, then save the quote
                {outcome.allowAddToCart !== false ? " or add it to the cart" : ""}
                .
              </p>
            </div>
          )}
          {outcome?.outcome === "quoted" && quoteInfo && (
            <div className="mt-4 flex items-start gap-2 rounded-xl border border-green-200 bg-green-50 p-4">
              <CheckCircleIcon className="h-5 w-5 shrink-0 text-green-600" />
              <div className="min-w-0">
                <p className="text-sm text-gray-800">
                  {outcome.keepSourcing ? (
                    <>
                      Quote{" "}
                      <Link
                        className="font-medium text-blue-700 hover:underline"
                        to={`/quotes/${quoteInfo.id}`}
                      >
                        {quoteInfo.reference}
                      </Link>{" "}
                      was built from the platform items that match exactly
                      and any replacements you selected. Vendors were not
                      emailed.
                      {(outcome.remainingLineIds || []).length > 0
                        ? " Some items still need a replacement selected or an RFQ sent to vendors — those options stay available below."
                        : " You can still send RFQs to vendors if you want to confirm stock, price, or lead time."}
                    </>
                  ) : (
                    <>
                      Everything in your RFQ is available. Quote{" "}
                      <Link
                        className="font-medium text-blue-700 hover:underline"
                        to={`/quotes/${quoteInfo.id}`}
                      >
                        {quoteInfo.reference}
                      </Link>{" "}
                      was generated with the best{" "}
                      {priority === "lead-time"
                        ? "delivery times"
                        : priority === "show-all"
                        ? "available options"
                        : "prices"}{" "}
                      we have — open it to review
                      {outcome.allowAddToCart !== false
                        ? ", add it to the cart"
                        : ""}
                      {outcome.allowSaveQuote !== false
                        ? ", or save it and convert it to an order later"
                        : ""}
                      .
                    </>
                  )}
                </p>
                <QuoteShareActions quote={quoteInfo} isKonoTeam={isKonoTeam} />
              </div>
            </div>
          )}
          {outcome?.outcome === "quoted-partial" && (
            <div className="mt-4 flex items-start gap-2 rounded-xl border border-green-200 bg-green-50 p-4">
              <CheckCircleIcon className="h-5 w-5 shrink-0 text-green-600" />
              <div className="min-w-0">
                <p className="text-sm text-gray-800">
                  Some items are available now
                  {quoteInfo ? (
                    <>
                      {" "}
                      on quote{" "}
                      <Link
                        className="font-medium text-blue-700 hover:underline"
                        to={`/quotes/${quoteInfo.id}`}
                      >
                        {quoteInfo.reference}
                      </Link>
                    </>
                  ) : null}
                  . We are still sourcing the rest
                  {outcome.comebackBusinessDays
                    ? ` and will come back within ${outcome.comebackBusinessDays} business day${
                        outcome.comebackBusinessDays === 1 ? "" : "s"
                      }`
                    : ""}
                  .
                </p>
                {quoteInfo && (
                  <QuoteShareActions quote={quoteInfo} isKonoTeam={isKonoTeam} />
                )}
              </div>
            </div>
          )}
          {outcome?.outcome === "waiting-vendors" && (
            <div className="mt-4 flex items-start gap-2 rounded-xl border border-amber-200 bg-amber-50 p-4">
              <ExclamationTriangleIcon className="h-5 w-5 shrink-0 text-amber-600" />
              <p className="text-sm text-gray-800">
                {isKonoTeam ? (
                  <>
                    Vendors are being asked to quote. Nothing goes to the
                    customer automatically — you will be emailed as replies
                    arrive, and the quote is sent when you press "Generate
                    quote" below.
                  </>
                ) : (
                  <>
                    Part of your RFQ is not on the platform right now. We are
                    sourcing it from our vendor network and will email your
                    complete quote within {outcome.maxQuoteBusinessHours}{" "}
                    business hours
                    {deadlineText ? ` — by ${deadlineText}` : ""}.
                  </>
                )}
              </p>
            </div>
          )}
          </div>
          {/* Offer to keep what they typed that the profile was missing. */}
          {profilePrompt && (
            <div className="mt-4 rounded-xl border border-blue-200 bg-blue-50 p-4 text-sm text-gray-800">
              <p>
                Save{" "}
                {Object.keys(profilePrompt)
                  .map((k) => PROFILE_UPDATE_LABELS[k] || k)
                  .join(", ")}{" "}
                to your profile? We would prefill it for you next time.
              </p>
              <ul className="mt-2 list-inside list-disc text-xs text-gray-600">
                {Object.entries(profilePrompt).map(([k, v]) => (
                  <li key={k}>
                    {(PROFILE_UPDATE_LABELS[k] || k).replace(/^your /, "")}
                    {": "}
                    <span className="font-medium text-gray-800">
                      {formatProfileValue(k, v)}
                    </span>
                  </li>
                ))}
              </ul>
              <div className="mt-3 flex gap-2">
                <button
                  onClick={() => void saveProfileUpdates()}
                  disabled={profileSaving}
                  className="rounded-lg bg-[#00062e] px-3 py-1.5 text-xs font-medium text-white hover:bg-[#001858] disabled:opacity-50"
                >
                  {profileSaving ? "Saving..." : "Yes, update my profile"}
                </button>
                <button
                  onClick={() => setProfilePrompt(null)}
                  disabled={profileSaving}
                  className="rounded-lg border border-gray-300 px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                >
                  No thanks
                </button>
              </div>
            </div>
          )}
          {profileNote && (
            <p className="mt-2 text-xs text-gray-600">{profileNote}</p>
          )}
          {/* Kono team: where this request stands right now, in one line. */}
          {isKonoTeam && currentId && statusFacts && (
            <div className="mt-3 flex flex-wrap items-center gap-x-3 gap-y-1 rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-xs text-gray-700">
              {(() => {
                const badge = statusBadge(currentStatus, true);
                return (
                  <span className={`rounded px-1.5 py-0.5 font-medium ${badge.cls}`}>
                    {currentStatus === "closed"
                      ? `Closed${statusFacts.closedReason ? ` (${statusFacts.closedReason === "deadline" ? "no stock at the deadline" : statusFacts.closedReason === "operator" ? "by an operator" : statusFacts.closedReason})` : ""}`
                      : currentStatus === "waiting-vendors"
                        ? `Sourcing${deadlineText ? ` - vendors answer by ${deadlineText}` : ""}`
                        : currentStatus === "quoted"
                          ? "Quoted - awaiting the customer"
                          : currentStatus === "converted"
                            ? "Ordered"
                            : currentStatus === "submitted"
                              ? "Submitted - customer choosing options"
                              : "Draft"}
                  </span>
                );
              })()}
              {quoteInfo && (
                <span>
                  Quote{" "}
                  <a className="font-medium text-indigo-700 hover:underline" href={`/quotes/${quoteInfo.id}`}>
                    {quoteInfo.reference}
                  </a>
                  {currentStatus === "quoted" && (
                    <>
                      {" "}
                      <a
                        className="rounded border border-indigo-300 px-1.5 py-0.5 text-[11px] font-medium text-indigo-700 hover:bg-indigo-50"
                        href={`${internalPortalUrl()}/rfqs?respond=${encodeURIComponent(currentId)}`}
                        target="_blank"
                        rel="noreferrer"
                        title="Change the quote from the internal portal: update it in place if it was never emailed, send a revised version (price, lead time, availability), or an additional quote for the remaining items"
                      >
                        Revise quote
                      </a>
                    </>
                  )}
                </span>
              )}
              <span>Uploaded by {statusFacts.uploadedBy}</span>
              <span>Assigned {statusFacts.assignedTo || "nobody yet"}</span>
              <span className="text-gray-500">
                Received {new Date(statusFacts.createdAt).toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}
                {statusFacts.updatedAt && statusFacts.updatedAt !== statusFacts.createdAt
                  ? ` - updated ${new Date(statusFacts.updatedAt).toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}`
                  : ""}
              </span>
            </div>
          )}
          {(buyerOptions?.length || inlineQuote) &&
            ["quoted", "submitted", "waiting-vendors"].includes(
              currentStatus,
            ) && (
              <RfqQuotePanel
                rfqId={currentId}
                lines={lines}
                options={buyerOptions || []}
                quote={inlineQuote}
                showOptions={
                  !inlineQuote &&
                  (quoteFlags.showOptions || priority === "show-all")
                }
                allowAddToCart={quoteFlags.allowAddToCart}
                allowSaveQuote={quoteFlags.allowSaveQuote}
                onQuote={(q) => {
                  setInlineQuote(q);
                  setQuoteInfo({ id: q.id, reference: q.reference });
                  if (currentStatus === "submitted") setCurrentStatus("quoted");
                }}
                onLines={(next) => {
                  setLines(next);
                  setDirty(true);
                }}
                onError={setError}
              />
            )}
          {isKonoTeam && unknownLineIds.length > 0 && currentId && (
            <RfqUnknownItemPanel
              rfqId={currentId}
              onSent={() => {
                setUnknownLineIds([]);
                void openRfq(currentId);
              }}
              onError={setError}
            />
          )}
          {currentStatus === "waiting-vendors" && !outcome && !isKonoTeam && (
            <div className="mt-4 rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-gray-800">
              We are sourcing part of this RFQ from vendors
              {deadlineText ? `; your quote arrives by ${deadlineText}` : ""}.
            </div>
          )}

          {/* A closed RFQ an operator can put back in front of the vendors. */}
          {isKonoTeam && currentStatus === "closed" && (
            <div className="mt-4 rounded-xl border border-gray-300 bg-gray-50 p-4 text-sm text-gray-800">
              <p className="font-medium">
                This RFQ is closed{quoteInfo ? "" : " and no quote was produced"}.
              </p>
              <p className="mt-1 text-xs text-gray-600">
                Reopen it to put it back in front of the vendors: their replies
                are recognized again and you can generate the quote once an item
                is quotable. Nothing is emailed to anyone when you reopen.
              </p>
              <button
                type="button"
                disabled={reopening}
                onClick={() => void reopenNow()}
                className="mt-2 rounded-lg border border-indigo-300 bg-white px-3 py-1.5 text-xs font-medium text-indigo-800 hover:bg-indigo-50 disabled:opacity-50"
              >
                {reopening ? "Reopening…" : "Reopen this RFQ"}
              </button>
            </div>
          )}

          {/* ------------------- Kono internal: outreach board + the button */}
          {isKonoTeam && currentStatus === "waiting-vendors" && (
            <div className="mt-4 rounded-xl border border-indigo-200 bg-indigo-50/40 p-4">
              <div className="flex items-center justify-between gap-3">
                <h3 className="text-sm font-semibold text-[#00062e]">
                  Vendor outreach{" "}
                  <span className="ml-1 rounded bg-indigo-100 px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide text-indigo-700">
                    Kono internal
                  </span>
                </h3>
                <button
                  type="button"
                  className="rounded-lg bg-teal-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-teal-700 disabled:opacity-50"
                  disabled={generatingQuote}
                  onClick={() => void generateQuoteNow()}
                >
                  {generatingQuote
                    ? "Generating…"
                    : "Generate quote & prepare customer email"}
                </button>
              </div>
              <p className="mt-1 text-xs text-gray-600">
                Replies land in inventory automatically and you are emailed
                each time. "Generate quote" creates the quote and prepares
                the customer email for you to review and send; it does not
                email the customer by itself. Or choose below: send the quote
                now, auto-send at the deadline, or hold for a manual send.
                {deadlineText ? ` Promised deadline: ${deadlineText}.` : ""}
              </p>

              {quoteAttempt && (
                <div className="mt-3 rounded-lg border border-amber-300 bg-amber-50 p-3 text-xs text-amber-900">
                  <p className="font-semibold">
                    No quote was generated — the RFQ is still open and nothing
                    was sent to the customer.
                  </p>
                  {quoteAttempt.lines.map((line) => (
                    <div key={line.lineId} className="mt-2">
                      <p className="font-medium">{line.description}</p>
                      {line.rejected.length ? (
                        <>
                          <p className="mt-0.5">
                            Vendors answered, but their quotes cannot be used as
                            they stand:
                          </p>
                          <ul className="mt-1 list-disc pl-5">
                            {line.rejected.map((item) => (
                              <li key={`${line.lineId}-${item.productId}`}>
                                <span className="font-medium">
                                  {item.supplierName || "Unknown vendor"}
                                </span>{" "}
                                — {item.summary}
                                {item.price ? ` (${item.price})` : ""}:{" "}
                                {item.reasons.join("; ")}
                              </li>
                            ))}
                          </ul>
                          <p className="mt-1">
                            In Sourcing below, pick one of those items as the
                            replacement for this item, or correct it in
                            inventory, then generate the quote again.
                          </p>
                        </>
                      ) : (
                        <p className="mt-0.5">
                          No vendor quote has arrived for this item yet.
                        </p>
                      )}
                    </div>
                  ))}
                </div>
              )}
              <div className="mt-3 flex flex-wrap gap-2">
                {(
                  [
                    ["send-now", "Send quote now"],
                    ["send-at-deadline", "Auto-send at deadline"],
                    ["hold", "Hold for manual send"],
                  ] as const
                ).map(([value, label]) => (
                  <button
                    key={value}
                    type="button"
                    disabled={savingChoice || generatingQuote}
                    onClick={() => void chooseFulfillment(value)}
                    className={`rounded-lg border px-3 py-1.5 text-xs font-medium ${
                      fulfillmentChoice === value
                        ? "border-teal-500 bg-teal-50 text-teal-800"
                        : "border-gray-300 bg-white text-gray-700 hover:bg-gray-50"
                    } disabled:opacity-50`}
                  >
                    {savingChoice && value === "send-now"
                      ? "Sending…"
                      : label}
                  </button>
                ))}
              </div>
              {partialDeadlineOpen && (
                <div className="mt-3 rounded-lg border border-amber-200 bg-white p-3">
                  <p className="text-xs font-medium text-gray-800">
                    Deadline passed with a partial fill. Send the quote for
                    what is available, or a custom email to the customer.
                  </p>
                  <div className="mt-2 flex flex-wrap gap-2">
                    <button
                      type="button"
                      disabled={sendingPartialDecision}
                      onClick={() => void decidePartialDeadline("send-quote")}
                      className="rounded-lg bg-teal-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-teal-700 disabled:opacity-50"
                    >
                      {sendingPartialDecision
                        ? "Sending…"
                        : "Send quote with what is available"}
                    </button>
                  </div>
                  <textarea
                    className="mt-2 w-full rounded-md border border-gray-300 px-2 py-1.5 text-xs"
                    rows={4}
                    value={partialCustomBody}
                    onChange={(e) => setPartialCustomBody(e.target.value)}
                    placeholder="Custom email to the customer"
                  />
                  <button
                    type="button"
                    disabled={sendingPartialDecision}
                    onClick={() => void decidePartialDeadline("custom-email")}
                    className="mt-2 rounded-lg border border-gray-300 bg-white px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                  >
                    Send custom email
                  </button>
                </div>
              )}
              {vendorRequestList}
            </div>
          )}
          <GenerateQuoteCheckDialog
            open={!!quoteCheck}
            readiness={quoteCheck?.readiness || null}
            action={quoteCheck?.action === "send-now" ? "Send the quote" : quoteCheck?.action === "regenerate" ? "Quote again" : "Generate quote"}
            busy={generatingQuote || savingChoice}
            onCancel={() => setQuoteCheck(null)}
            onProceed={() => {
              const action = quoteCheck?.action;
              setQuoteCheck(null);
              if (action === "generate") void generateQuoteNow(true);
              else if (action === "send-now") void chooseFulfillment("send-now", true);
              else if (action === "regenerate") void regenerateQuoteNow(true);
            }}
          />
          {/* Customers (not Kono): the quote link and the share actions. */}
          {!isKonoTeam && quoteInfo && currentStatus !== "waiting-vendors" && !outcome && (
            <div className="mt-4 rounded-xl border border-green-200 bg-green-50 p-4 text-sm text-gray-800">
              This RFQ has quote{" "}
              <Link
                className="font-medium text-blue-700 hover:underline"
                to={`/quotes/${quoteInfo.id}`}
              >
                {quoteInfo.reference}
              </Link>
              .
              <QuoteShareActions quote={quoteInfo} isKonoTeam={isKonoTeam} />
            </div>
          )}

          {isKonoTeam && currentId && currentStatus === "waiting-vendors" && (
            <CustomerEmailsPanel rfqId={currentId} refreshKey={customerEmailKey} onError={setError} hideQuoteEmail={false} />
          )}

          {/* ------------------------------------------------------------------
              Kono internal, once the RFQ is past sourcing (quoted, submitted,
              ordered, closed): the customer's side on the left, the vendors'
              on the right. One place per thing: the quote link lives in the
              status line above and in the Customer column header, nowhere
              else; the thread, the last step and the proposed next step are
              in the Customer column; the vendors' answers, the lower price
              option and "ask more vendors" in the Vendors column. */}
          {isKonoTeam && currentId && currentStatus !== "waiting-vendors" && (vendorRequests?.length || quoteInfo) && (
            <div className="mt-4 grid gap-4 xl:grid-cols-2">
              <section className="flex min-w-0 flex-col gap-3 rounded-xl border border-emerald-200 bg-emerald-50/30 p-4">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <h3 className="text-sm font-semibold text-[#00062e]">
                    Customer communication
                    <span className="ml-1 rounded bg-indigo-100 px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide text-indigo-700">Kono internal</span>
                  </h3>
                  {quoteInfo && (
                    <div className="flex flex-wrap items-center gap-2 text-xs">
                      <Link className="font-medium text-indigo-700 hover:underline" to={`/quotes/${quoteInfo.id}`} target="_blank">
                        Quote {quoteInfo.reference}
                      </Link>
                      <QuoteShareActions quote={quoteInfo} isKonoTeam={isKonoTeam} />
                    </div>
                  )}
                </div>
                <NextStepCard
                  rfqId={currentId}
                  status={currentStatus}
                  quoteReference={quoteInfo?.reference || null}
                  customerEmailState={customerEmailState}
                  thread={emailThread}
                  betterPrice={betterPrice}
                  vendorsWaiting={(vendorRequests || []).filter((r) => r.status === "sent").length}
                  vendorsResponded={(vendorRequests || []).filter((r) => r.status === "responded").length}
                  onSuggested={() => setCustomerEmailKey((k) => k + 1)}
                  onError={setError}
                />
                {quoteInfo && (
                  <CustomerEmailPanel rfqId={currentId} quoteId={quoteInfo.id} refreshKey={customerEmailKey} onError={setError} onState={setCustomerEmailState} />
                )}
                <EmailThreadPanel rfqId={currentId} refreshKey={customerEmailKey} onLoaded={setEmailThread} />
                <CustomerEmailsPanel
                  rfqId={currentId}
                  refreshKey={customerEmailKey}
                  onError={setError}
                  hideQuoteEmail={Boolean(quoteInfo)}
                />
              </section>

              <section className="flex min-w-0 flex-col gap-3 rounded-xl border border-indigo-200 bg-indigo-50/40 p-4">
                <h3 className="text-sm font-semibold text-[#00062e]">
                  Vendor communication
                  <span className="ml-1 rounded bg-indigo-100 px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide text-indigo-700">Kono internal</span>
                </h3>
                <p className="text-xs text-gray-600">
                  Every vendor asked for this RFQ and what each answered, at any stage (quoted, ordered or closed). Click a row to read the vendor's own words and reply to them from here; a row in <span className="font-semibold text-red-700">red</span> is a vendor who answered after the quote with a lower price or an item the quote did not cover.
                  {currentStatus === "converted" ? " The order is placed; vendors can still be asked (a better price may serve the next order or a revised one)." : ""}
                </p>
                {quoteInfo && (
                  <BetterPricePanel
                    rfqId={currentId}
                    initial={betterPrice}
                    refreshKey={customerEmailKey}
                    onThankVendor={(requestId) => setReplyingTo(requestId)}
                    onRevised={() => {
                      void openRfq(currentId);
                      setCustomerEmailKey((k) => k + 1);
                    }}
                    onError={setError}
                  />
                )}
                {vendorRequestList}
                <div className="flex flex-wrap items-center gap-3 border-t border-indigo-200 pt-3">
                  {currentStatus === "quoted" && (
                    <button
                      type="button"
                      className="rounded-lg bg-teal-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-teal-700 disabled:opacity-50"
                      disabled={generatingQuote}
                      onClick={() => void regenerateQuoteNow()}
                    >
                      {generatingQuote ? "Generating..." : "Quote again from the current picks"}
                    </button>
                  )}
                  <p className="text-xs text-gray-600">
                    To ask more vendors (also after the customer has the quote or the order): tick them under "Vendors to RFQ" on the item below and press "Ask more vendors". A reply that beats the quote shows up here in red and as a lower price option; "Quote again" makes the next quote version and prepares the customer email.
                  </p>
                </div>
              </section>
            </div>
          )}

          {/* --------------------------------------------------- the editor */}
          {hasWork && (
            <div className="mt-4 flex flex-col gap-4">
              {sourceFile?.filename && (
                <p className="text-xs text-gray-500">
                  Extracted from <span className="font-medium">{sourceFile.filename}</span>
                </p>
              )}
              {sourceText && (
                <details className="rounded-xl border border-gray-200 bg-gray-50 p-3">
                  <summary className="cursor-pointer text-xs font-medium text-gray-600">
                    Original request
                  </summary>
                  <pre className="mt-2 max-h-64 overflow-auto whitespace-pre-wrap text-xs text-gray-700">
                    {sourceText}
                  </pre>
                </details>
              )}

              {/* Customer */}
              <section className="rounded-xl border border-gray-200 p-4">
                <h3 className="text-sm font-semibold text-[#00062e]">
                  {isKonoTeam ? "Customer" : "Your details"}
                </h3>
                {isKonoTeam && (
                  <p className="mt-1 text-xs text-gray-500">
                    {pickedCustomer
                      ? `Filled from ${pickedCustomer.sourceLabel.toLowerCase()}: ${pickedCustomer.company || pickedCustomer.name || pickedCustomer.email}. Edit any field if something is off.`
                      : "Start typing a name, company or email: known customers (kono.ai users, earlier RFQs, CRM accounts) are suggested; pick one to fill every field."}
                  </p>
                )}
                <div className="mt-2 grid grid-cols-1 gap-3 sm:grid-cols-2">
                  {(["name", "company", "email"] as const).map((field) => (
                    <div key={field} className="relative">
                      <label className={labelCls}>{field === "name" ? "Name" : field === "company" ? "Company" : "Email"}</label>
                      <input
                        className={inputCls}
                        value={customer[field]}
                        autoComplete="off"
                        onChange={(e) => {
                          setCustomer({ ...customer, [field]: e.target.value });
                          setDirty(true);
                        }}
                        onFocus={() => isKonoTeam && setLookupField(field)}
                        onBlur={() => setTimeout(() => setLookupField((f) => (f === field ? null : f)), 150)}
                        onKeyDown={(e) => {
                          if (e.key === "Escape") setLookupField(null);
                        }}
                      />
                      {isKonoTeam && lookupField === field && (
                        <CustomerSuggestions
                          query={customer[field]}
                          hits={lookup.hits}
                          searching={lookup.searching}
                          failed={lookup.failed}
                          onPick={applyCustomerPick}
                        />
                      )}
                    </div>
                  ))}
                  <div>
                    <label className={labelCls}>Phone</label>
                    <input
                      className={inputCls}
                      value={customer.phone}
                      onChange={(e) => {
                        setCustomer({ ...customer, phone: e.target.value });
                        setDirty(true);
                      }}
                    />
                  </div>
                </div>
                {isKonoTeam && (
                  <CustomerWebsite
                    rfqId={currentId}
                    website={customer.website}
                    email={customer.email}
                    onChecked={(website) => setCustomer({ ...customer, website })}
                  />
                )}
                <div className="mt-4">
                  <p className="text-xs font-medium text-gray-500">
                    Ship-to address
                  </p>
                  <p className="mt-0.5 text-xs text-gray-500">
                    Used to price freight on the quote. Street, city, postal
                    code and country are required for a carrier rate.
                  </p>
                  <div className="relative mt-2">
                    <label className={labelCls}>Search address</label>
                    <AddressAutocomplete
                      placeholder="Start typing a street address"
                      inputClassName={inputCls}
                      dropdownClassName="absolute z-20 mt-1 w-full rounded-lg border border-gray-300 bg-white text-sm shadow-lg"
                      onAddressSelected={(address) => {
                        setCustomer({
                          ...customer,
                          address: {
                            ...emptyAddress(),
                            ...address,
                            streetLines: [
                              address.streetLines?.[0] || "",
                              address.streetLines?.[1] || "",
                            ],
                            countryCode: address.countryCode || "US",
                          },
                        });
                        setDirty(true);
                      }}
                    />
                  </div>
                  <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2">
                    <div className="sm:col-span-2">
                      <label className={labelCls}>Street</label>
                      <input
                        className={inputCls}
                        value={addressOf(customer).streetLines[0] || ""}
                        onChange={(e) => {
                          setCustomer({
                            ...customer,
                            address: withStreetLine(
                              addressOf(customer),
                              0,
                              e.target.value,
                            ),
                          });
                          setDirty(true);
                        }}
                      />
                    </div>
                    <div className="sm:col-span-2">
                      <label className={labelCls}>
                        Apt, suite, unit (optional)
                      </label>
                      <input
                        className={inputCls}
                        value={addressOf(customer).streetLines[1] || ""}
                        onChange={(e) => {
                          setCustomer({
                            ...customer,
                            address: withStreetLine(
                              addressOf(customer),
                              1,
                              e.target.value,
                            ),
                          });
                          setDirty(true);
                        }}
                      />
                    </div>
                    <div>
                      <label className={labelCls}>City</label>
                      <input
                        className={inputCls}
                        value={addressOf(customer).city}
                        onChange={(e) => {
                          setCustomer({
                            ...customer,
                            address: {
                              ...addressOf(customer),
                              city: e.target.value,
                            },
                          });
                          setDirty(true);
                        }}
                      />
                    </div>
                    <div>
                      <label className={labelCls}>State / Province</label>
                      <input
                        className={inputCls}
                        value={addressOf(customer).stateOrProvinceCode || ""}
                        onChange={(e) => {
                          setCustomer({
                            ...customer,
                            address: {
                              ...addressOf(customer),
                              stateOrProvinceCode: e.target.value,
                            },
                          });
                          setDirty(true);
                        }}
                      />
                    </div>
                    <div>
                      <label className={labelCls}>Postal code</label>
                      <input
                        className={inputCls}
                        value={addressOf(customer).postalCode}
                        onChange={(e) => {
                          setCustomer({
                            ...customer,
                            address: {
                              ...addressOf(customer),
                              postalCode: e.target.value,
                            },
                          });
                          setDirty(true);
                        }}
                      />
                    </div>
                    <div>
                      <label className={labelCls}>Country</label>
                      <input
                        className={inputCls}
                        value={addressOf(customer).countryCode}
                        onChange={(e) => {
                          setCustomer({
                            ...customer,
                            address: {
                              ...addressOf(customer),
                              countryCode: e.target.value,
                            },
                          });
                          setDirty(true);
                        }}
                      />
                    </div>
                  </div>
                </div>
              </section>

              {/* Lines */}
              <section className="flex flex-col gap-3">
                <div className="flex items-center justify-between">
                  <h3 className="text-sm font-semibold text-[#00062e]">
                    Requested materials ({lines.length})
                  </h3>
                  <button
                    onClick={() => {
                      setLines((prev) => [...prev, newLine()]);
                      setDirty(true);
                    }}
                    disabled={editingLocked}
                    className="inline-flex items-center gap-1 text-xs font-medium text-teal-700 hover:underline disabled:opacity-50"
                  >
                    <PlusIcon className="h-3.5 w-3.5" /> Add item
                  </button>
                </div>

                {linesWithMissing.map((line, index) => {
                  const dims =
                    DIMENSIONS_BY_SHAPE[line.shape.trim().toLowerCase()] ||
                    Object.keys(line.dimensions);
                  const missingSet = new Set(
                    line.missingFields.map((f) =>
                      f.startsWith("dimension:") ? f.slice("dimension:".length) : f,
                    ),
                  );
                  const choiceMissing = line.missingFields.find((f) =>
                    f.startsWith("dimension-choice:"),
                  );
                  return (
                    <div
                      key={line.id}
                      className={`rounded-xl border p-4 ${
                        line.missingFields.length
                          ? "border-red-200"
                          : "border-gray-200"
                      }`}
                    >
                      <div className="flex items-start justify-between gap-2">
                        <p className="text-xs font-medium text-gray-400">
                          Item {index + 1}
                          {line.rawText && (
                            <span className="ml-2 font-normal italic text-gray-400">
                              "{line.rawText.slice(0, 90)}
                              {line.rawText.length > 90 ? "..." : ""}"
                            </span>
                          )}
                        </p>
                        <button
                          onClick={() => {
                            setLines((prev) => prev.filter((l) => l.id !== line.id));
                            setDirty(true);
                          }}
                          disabled={editingLocked}
                          className="rounded p-1 text-gray-300 hover:text-red-600 disabled:opacity-40"
                          aria-label="Remove item"
                        >
                          <TrashIcon className="h-4 w-4" />
                        </button>
                      </div>

                      {line.missingFields.length > 0 && (
                        <p className="mt-1 text-xs font-medium text-red-600">
                          Missing:{" "}
                          {line.missingFields.map(missingFieldLabel).join(", ")}
                        </p>
                      )}

                      <div className="mt-2 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
                        <div>
                          <label className={labelCls}>Material *</label>
                          <input
                            className={`${inputCls} ${
                              missingSet.has("material") ? "border-red-400" : ""
                            }`}
                            autoComplete="off"
                            value={line.material}
                            disabled={editingLocked}
                            onChange={(e) =>
                              patchLine(line.id, { material: e.target.value })
                            }
                          />
                        </div>
                        <div>
                          <label className={labelCls}>Shape *</label>
                          <select
                            className={`${inputCls} ${
                              missingSet.has("shape") ? "border-red-400" : ""
                            }`}
                            value={line.shape}
                            disabled={editingLocked}
                            onChange={(e) =>
                              patchLine(line.id, { shape: e.target.value })
                            }
                          >
                            <option value="">Select...</option>
                            {SHAPE_OPTIONS.map((s) => (
                              <option key={s} value={s}>
                                {s}
                              </option>
                            ))}
                          </select>
                        </div>
                        <div>
                          <label className={labelCls}>Quantity *</label>
                          <input
                            className={`${inputCls} ${
                              missingSet.has("quantity") ? "border-red-400" : ""
                            }`}
                            autoComplete="off"
                            value={line.quantity}
                            disabled={editingLocked}
                            onChange={(e) =>
                              patchLine(line.id, { quantity: e.target.value })
                            }
                          />
                        </div>
                        <div>
                          <label className={labelCls}>Unit</label>
                          <input
                            className={inputCls}
                            placeholder="pieces"
                            // "Unit" reads like an apartment line to address
                            // autofill; picking a saved address sprayed the
                            // street into every line's unit box.
                            autoComplete="off"
                            name={`rfq-quantity-unit-${index}`}
                            value={line.quantityUnit}
                            disabled={editingLocked}
                            onChange={(e) =>
                              patchLine(line.id, { quantityUnit: e.target.value })
                            }
                          />
                        </div>

                        {dims.map((dim) => (
                          <div key={dim}>
                            <label className={labelCls}>
                              {dim.replace(/_/g, " ")}
                              {dimensionRequirement(line.shape, dim) === "required"
                                ? " *"
                                : dimensionRequirement(line.shape, dim) === "any-two"
                                  ? " * (any two)"
                                  : ""}
                            </label>
                            <input
                              className={`${inputCls} ${
                                missingSet.has(dim) ||
                                (choiceMissing &&
                                  ["outer_diameter", "inner_diameter", "wall_thickness"].includes(dim))
                                  ? "border-red-400"
                                  : ""
                              }`}
                              placeholder={'e.g. 2.5"'}
                              autoComplete="off"
                              value={line.dimensions[dim] || ""}
                              disabled={editingLocked}
                              onChange={(e) =>
                                patchDimension(line.id, dim, e.target.value)
                              }
                            />
                          </div>
                        ))}

                        <div>
                          <label className={labelCls}>
                            Standard / spec *
                            {line.standardSuggested && line.standard.trim() && (
                              <span className="ml-1 rounded bg-amber-100 px-1 py-0.5 text-[0.6rem] font-semibold uppercase tracking-wide text-amber-800">
                                suggested
                              </span>
                            )}
                          </label>
                          <input
                            className={`${inputCls} ${
                              missingSet.has("standard")
                                ? "border-red-400"
                                : line.standardSuggested
                                  ? "border-amber-400 bg-amber-50"
                                  : ""
                            }`}
                            placeholder={standardSuggesting[line.id] ? "Looking up the usual spec..." : "AMS 5662..."}
                            autoComplete="off"
                            value={line.standard}
                            disabled={editingLocked}
                            onChange={(e) =>
                              patchLine(line.id, { standard: e.target.value, standardSuggested: false })
                            }
                          />
                          {line.standardSuggested && line.standard.trim() && (
                            <p className="mt-1 text-[0.65rem] leading-snug text-amber-800">
                              Suggested by AI from the material and shape, not
                              taken from your request. Please confirm it or type
                              the spec your drawing calls for.
                            </p>
                          )}
                        </div>
                        <div>
                          <label className={labelCls}>Condition</label>
                          <input
                            className={inputCls}
                            placeholder="annealed..."
                            autoComplete="off"
                            value={line.condition}
                            disabled={editingLocked}
                            onChange={(e) =>
                              patchLine(line.id, { condition: e.target.value })
                            }
                          />
                          {line.condition.trim() && (
                            <label
                              className="mt-1 flex items-center gap-1 text-[0.65rem] text-gray-600"
                              title="Tick when the customer said the condition is not required (with or without the heat treat). Stock in another condition then counts as an exact fill and vendors are told either is fine."
                            >
                              <input
                                type="checkbox"
                                checked={line.conditionOptional === true}
                                disabled={editingLocked}
                                onChange={(e) =>
                                  patchLine(line.id, { conditionOptional: e.target.checked })
                                }
                              />
                              optional (with or without is fine)
                            </label>
                          )}
                        </div>
                        <div>
                          <label className={labelCls}>Origin (DFARS...)</label>
                          <input
                            className={inputCls}
                            autoComplete="off"
                            value={line.originCompliance}
                            disabled={editingLocked}
                            onChange={(e) =>
                              patchLine(line.id, { originCompliance: e.target.value })
                            }
                          />
                        </div>
                        <div>
                          <label className={labelCls}>Target price</label>
                          <input
                            className={inputCls}
                            placeholder="$/lb, $/ft, $/each..."
                            autoComplete="off"
                            value={line.targetPrice}
                            disabled={editingLocked}
                            onChange={(e) =>
                              patchLine(line.id, { targetPrice: e.target.value })
                            }
                          />
                        </div>
                        <div className="col-span-2">
                          <label className={labelCls}>Additional specifications</label>
                          <input
                            className={inputCls}
                            autoComplete="off"
                            value={line.additionalSpecifications}
                            disabled={editingLocked}
                            onChange={(e) =>
                              patchLine(line.id, {
                                additionalSpecifications: e.target.value,
                              })
                            }
                          />
                        </div>
                        <div className="col-span-2">
                          <label className={labelCls}>
                            Additional work (cutting...)
                          </label>
                          <input
                            className={inputCls}
                            autoComplete="off"
                            value={line.additionalWork}
                            disabled={editingLocked}
                            onChange={(e) =>
                              patchLine(line.id, { additionalWork: e.target.value })
                            }
                          />
                        </div>
                      </div>
                    </div>
                  );
                })}
              </section>

              {/* Priority + logistics */}
              <section className="rounded-xl border border-gray-200 p-4">
                <h3 className="text-sm font-semibold text-[#00062e]">
                  What matters most?
                </h3>
                <p className="mt-0.5 text-xs text-gray-500">
                  When several options exist, this decides which one your quote
                  gets.
                </p>
                <div className="mt-2 flex gap-4">
                  {(
                    [
                      ["price", "Best price"],
                      ["lead-time", "Fastest delivery"],
                      ["show-all", "Show me all options"],
                    ] as [RfqPriority, string][]
                  ).map(([value, label]) => (
                    <label
                      key={value}
                      className="flex cursor-pointer items-center gap-2 text-sm text-[#00062e]"
                    >
                      <input
                        type="radio"
                        name="rfq-priority"
                        checked={priority === value}
                        disabled={editingLocked}
                        onChange={() => {
                          setPriority(value);
                          setDirty(true);
                        }}
                      />
                      {label}
                    </label>
                  ))}
                </div>
                <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2">
                  <div>
                    <label className={labelCls}>Needed by</label>
                    <input
                      className={inputCls}
                      placeholder="e.g. Sep 15, or ASAP"
                      value={neededBy}
                      disabled={editingLocked}
                      onChange={(e) => {
                        setNeededBy(e.target.value);
                        setDirty(true);
                      }}
                    />
                  </div>
                  <div>
                    <label className={labelCls}>Deliver to</label>
                    <input
                      className={inputCls}
                      placeholder="City, state — freight uses the ship-to address above"
                      value={deliveryLocation}
                      disabled={editingLocked}
                      onChange={(e) => {
                        setDeliveryLocation(e.target.value);
                        setDirty(true);
                      }}
                    />
                  </div>
                </div>
                <div className="mt-3">
                  <label className={labelCls}>Notes</label>
                  <textarea
                    className={inputCls}
                    rows={2}
                    value={notes}
                    disabled={editingLocked}
                    onChange={(e) => {
                      setNotes(e.target.value);
                      setDirty(true);
                    }}
                  />
                </div>
              </section>

              {/* kono.ai: for every item — is it in inventory exactly as
                  requested, from whom, on what data; and when it is not,
                  which vendors to RFQ. Buyers never see this section. */}
              {isKonoTeam && (
                <section className="rounded-xl border border-indigo-200 bg-indigo-50/40 p-4">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <h3 className="text-sm font-semibold text-[#00062e]">
                      Sourcing{" "}
                      <span className="ml-1 rounded-full bg-indigo-100 px-2 py-0.5 text-[0.6rem] font-medium text-indigo-800">
                        Kono internal
                      </span>
                    </h3>
                    <button
                      onClick={() => void loadMatches()}
                      disabled={matchesLoading || saving}
                      className="rounded-lg border border-indigo-300 bg-white px-3 py-1.5 text-sm font-medium text-indigo-800 hover:bg-indigo-50 disabled:opacity-50"
                    >
                      {matchesLoading ? "Checking..." : "Refresh"}
                    </button>
                  </div>
                  <p className="mt-0.5 text-xs text-gray-600">
                    For every requested item: whether it is in inventory
                    exactly as requested, from which vendor, on what data,
                    and which vendors to RFQ. Convert to quote uses exact
                    matches and any close match you mark as a replacement.
                    It does not email vendors — use Preview / Send RFQs for
                    that. When nobody is on the platform, suggested sources
                    are proposed.
                  </p>
                  <p className="mt-0.5 text-xs text-gray-600">
                    {superUserActive ? (
                      <>
                        Super powers are on: use{" "}
                        <span className="font-medium">Edit item</span> on any
                        option to change its price, lead time, standard,
                        sizes or other fields. The change is made on the
                        inventory item itself, so it applies to this RFQ and
                        to every new transaction on that item until it is
                        changed again.
                      </>
                    ) : (
                      <>
                        To change an item's price, lead time, standard or
                        other fields from here, turn on super powers (the
                        black circle, bottom right).
                      </>
                    )}
                  </p>

                  {matchesLoading && !matches && (
                    <p className="mt-3 text-xs text-gray-500">
                      Checking inventory for every item...
                    </p>
                  )}

                  {matches && (
                    <div className="mt-3 flex flex-col gap-3">
                      {matches.map((entry) => {
                        const lineIndex = linesWithMissing.findIndex(
                          (l) => l.id === entry.lineId,
                        );
                        const line = linesWithMissing[lineIndex];
                        const removed = line
                          ? null
                          : removedLines.find((r) => r.id === entry.lineId) || null;
                        const label = line
                          ? [line.material, line.shape]
                              .filter(Boolean)
                              .join(" ") || `Item ${lineIndex + 1}`
                          : removed
                            ? `Removed item: ${removed.description}`
                            : "Removed item";
                        const chosenId = line?.preferredProductId || "";
                        const alsoOffered = line?.alsoOfferProductIds || [];
                        const verdictBadge =
                          entry.verdict === "exact"
                            ? {
                                text: "In inventory — exactly as requested",
                                cls: "bg-green-100 text-green-800",
                              }
                            : entry.verdict === "close"
                              ? {
                                  text: "Close matches only — not exactly as requested",
                                  cls: "bg-amber-100 text-amber-800",
                                }
                              : {
                                  text: "Not in inventory",
                                  cls: "bg-red-100 text-red-800",
                                };
                        return (
                          <div
                            key={entry.lineId}
                            className="rounded-lg border border-gray-200 bg-white p-3"
                          >
                            <div className="flex flex-wrap items-center gap-2">
                              <p className="text-xs font-semibold text-[#00062e]">
                                Item {lineIndex >= 0 ? lineIndex + 1 : "?"} —{" "}
                                {label}
                              </p>
                              {!line && (
                                <p className="w-full text-[0.7rem] text-gray-600">
                                  {removed
                                    ? `This item was taken off the request by ${removed.removedBy} on ${removed.removedAt
                                        .replace("T", " ")
                                        .slice(0, 16)} UTC. The matches below were found before that and are kept for reference; they are not quoted.`
                                    : "This item is no longer on the request (removed in an earlier edit, before removals were recorded). The matches below are kept for reference; they are not quoted."}
                                </p>
                              )}
                              <span
                                className={`rounded-full px-2 py-0.5 text-[0.65rem] font-medium ${verdictBadge.cls}`}
                              >
                                {verdictBadge.text}
                              </span>
                            </div>
                            {entry.verdict === "close" && !chosenId && (
                              <p className="mt-1 text-[0.7rem] text-amber-800">
                                Select a similar item as a replacement to
                                include it in Convert to quote. Otherwise
                                send an RFQ to vendors.
                              </p>
                            )}
                            {entry.verdict === "unavailable" && (
                              <p className="mt-1 text-[0.7rem] text-red-800">
                                No platform item to quote. Send an RFQ to
                                vendors, or wait for a close match you can
                                mark as a replacement.
                              </p>
                            )}
                            {line?.quotedIn && (
                              <p
                                className={`mt-1 rounded px-2 py-1 text-[0.7rem] ${
                                  entry.options.some((o) => o.productId === line.quotedIn?.productId)
                                    ? "bg-teal-50 text-teal-900"
                                    : "bg-amber-50 text-amber-900"
                                }`}
                                title="The item the customer's quote used. Regenerating the quote keeps it unless you pick another item here."
                              >
                                Quoted in {line.quotedIn.quoteReference} on{" "}
                                {line.quotedIn.at.replace("T", " ").slice(0, 16)} UTC:{" "}
                                {line.quotedIn.productSummary || line.quotedIn.productId}
                                {line.quotedIn.supplierName ? ` (${line.quotedIn.supplierName})` : ""}
                                {typeof line.quotedIn.unitPrice === "number"
                                  ? ` at ${moneyIn(line.quotedIn.unitPrice, line.quotedIn.currency)}`
                                  : ""}
                                {entry.options.some((o) => o.productId === line.quotedIn?.productId)
                                  ? ""
                                  : " - that item is no longer among the matches (sold, removed or repriced); pick a replacement before regenerating."}
                              </p>
                            )}
                            {entry.recommendation?.why && (
                              <p className="mt-1 rounded bg-indigo-50 px-2 py-1 text-[0.7rem] text-indigo-900">
                                Recommended: {entry.recommendation.why}
                              </p>
                            )}
                            {entry.options.map((option) => {
                        const isChosen = chosenId
                          ? option.productId === chosenId
                          : entry.verdict === "exact" &&
                            option.exactMatch &&
                            option.selected;
                        return (
                                <label
                                  key={option.productId}
                                  className={`mt-2 flex cursor-pointer items-start gap-2 rounded-lg border p-2.5 ${
                                    isChosen
                                      ? "border-teal-500 bg-teal-50/50"
                                      : "border-gray-200 hover:border-gray-300"
                                  }`}
                                >
                                  <input
                                    type="radio"
                                    className="mt-1"
                                    name={`rfq-match-${entry.lineId}`}
                                    checked={isChosen}
                                    disabled={replacementPickLocked}
                                    onChange={() =>
                                      patchLine(entry.lineId, {
                                        preferredProductId: option.productId,
                                      })
                                    }
                                  />
                                  <div className="min-w-0 text-xs">
                                    <p className="font-medium text-[#00062e]">
                                      {option.vendor.name || "Unknown vendor"}
                                      {option.recommended && (
                                        <span
                                          className="ml-2 rounded bg-indigo-100 px-1.5 py-0.5 text-[0.6rem] font-medium text-indigo-800"
                                          title="The platform's pick for this item: fit first, then price or lead time per the RFQ's priority, with what operators chose before."
                                        >
                                          recommended
                                        </span>
                                      )}
                                      {option.suggestion === "strong" && (
                                        <span
                                          className="ml-2 rounded bg-green-100 px-1.5 py-0.5 text-[0.6rem] font-medium text-green-800"
                                          title="An experienced purchaser (RFQ Configuration) chose this vendor before for this alloy and shape."
                                        >
                                          strong suggestion
                                        </span>
                                      )}
                                      {isKonoTeam && currentId && line && !isChosen && (
                                        <button
                                          type="button"
                                          className={`ml-2 rounded px-1.5 py-0.5 text-[0.6rem] font-medium ${
                                            alsoOffered.includes(option.productId)
                                              ? "bg-amber-100 text-amber-900 hover:bg-amber-200"
                                              : "border border-gray-300 text-gray-600 hover:bg-gray-100"
                                          }`}
                                          title={
                                            alsoOffered.includes(option.productId)
                                              ? "Offered on the quote as an alternative the customer can switch to. Click to stop offering it."
                                              : "Also offer this item on the quote, next to the chosen one, so the customer can pick between them."
                                          }
                                          disabled={replacementPickLocked}
                                          onClick={(event) => {
                                            event.preventDefault();
                                            void toggleAlsoOffer(line.id, option.productId, !alsoOffered.includes(option.productId));
                                          }}
                                        >
                                          {alsoOffered.includes(option.productId) ? "also offered - remove" : "also offer"}
                                        </button>
                                      )}
                                      {option.suggestion === "weak" && (
                                        <span
                                          className="ml-2 rounded bg-gray-100 px-1.5 py-0.5 text-[0.6rem] font-medium text-gray-600"
                                          title="Experienced purchasers passed this vendor over before for this alloy and shape."
                                        >
                                          weak suggestion
                                        </span>
                                      )}
                                      {option.price && (
                                        <span className="ml-2 font-semibold text-teal-700">
                                          {moneyIn(option.price.value, option.price.currency)}
                                          {option.price.unit
                                            ? `/${option.price.unit}`
                                            : ""}
                                        </span>
                                      )}
                                      {option.lineAmount != null && (
                                        <span className="ml-2 font-normal text-gray-500">
                                          line total {money(option.lineAmount)}
                                        </span>
                                      )}
                                      {option.pricePerLb ? (
                                        <span
                                          className={`ml-2 rounded px-1.5 py-0.5 text-[0.65rem] font-medium ${
                                            option.cheapestPerLb
                                              ? "bg-emerald-100 text-emerald-800"
                                              : "bg-gray-100 text-gray-700"
                                          }`}
                                          title={`Price per pound (${option.pricePerLb.basis}) so options priced per piece, per foot and per pound compare on one number.${
                                            option.cheapestPerLb ? " Lowest per pound among this line's options." : ""
                                          }`}
                                        >
                                          {moneyIn(option.pricePerLb.value, option.pricePerLb.currency)}/lb
                                          {option.cheapestPerLb ? " · cheapest" : ""}
                                        </span>
                                      ) : option.price && option.pricePerLbNote ? (
                                        <span
                                          className="ml-2 rounded bg-gray-50 px-1.5 py-0.5 text-[0.65rem] text-gray-500"
                                          title={`No price per pound: ${option.pricePerLbNote}.`}
                                        >
                                          $/lb n/a
                                        </span>
                                      ) : null}
                                      {leadTimeText(
                                        option.leadTimeDaysMin,
                                        option.leadTimeDaysMax,
                                      ) && (
                                        <span className="ml-2 font-normal text-gray-500">
                                          lead time{" "}
                                          {leadTimeText(
                                            option.leadTimeDaysMin,
                                            option.leadTimeDaysMax,
                                          )}
                                          {option.leadTimeSource ===
                                            "archive-observed" && (
                                            <span
                                              className="ml-1 rounded bg-amber-50 px-1 text-[0.6rem] text-amber-800"
                                              title="Lead time the vendor quoted to California Metals for this alloy; no lead time on the catalog line itself."
                                            >
                                              archive
                                            </span>
                                          )}
                                        </span>
                                      )}
                                    </p>
                                    {option.summary && (
                                      <p className="mt-0.5 truncate text-gray-600">
                                        {option.summary}
                                      </p>
                                    )}
                                    {option.sourcingNote && (
                                      <p
                                        className="mt-0.5 text-amber-800"
                                        title="Sourcing note on this inventory item — included in vendor RFQ emails and on the quote"
                                      >
                                        Sourcing note: {option.sourcingNote}
                                      </p>
                                    )}
                                    {option.exactMatch ? (
                                      <p className="mt-0.5 font-medium text-green-700">
                                        Exactly as requested
                                        {option.notes.length > 0 &&
                                          ` (${option.notes.join("; ")})`}
                                      </p>
                                    ) : option.facets?.length ? (
                                      <>
                                        <p className="mt-0.5 text-amber-700">
                                          {option.differences.length} difference
                                          {option.differences.length === 1 ? "" : "s"} from the request:
                                        </p>
                                        <FitTable facets={option.facets} />
                                      </>
                                    ) : (
                                      option.differences.length > 0 && (
                                        <p className="mt-0.5 text-amber-700">
                                          Differs: {option.differences.join("; ")}
                                        </p>
                                      )
                                    )}
                                    <p className="mt-0.5 text-gray-500">
                                      {option.ingestMethod}
                                      {option.obtainedAt
                                        ? ` · data obtained ${shortDate(option.obtainedAt)}`
                                        : ""}
                                      {" · "}
                                      {option.priceOrigin}
                                      {option.vendorOriginalPrice && (
                                        <span
                                          className="text-gray-600"
                                          title="What the vendor published; the price shown is what the quote uses."
                                        >
                                          {" "}
                                          (vendor's original price{" "}
                                          {moneyIn(option.vendorOriginalPrice.value, option.vendorOriginalPrice.currency)}
                                          {option.vendorOriginalPrice.unit
                                            ? `/${option.vendorOriginalPrice.unit}`
                                            : ""}
                                          {option.price &&
                                            option.price.value <
                                              option.vendorOriginalPrice.value &&
                                            `, ours is ${Math.round(
                                              (1 -
                                                option.price.value /
                                                  option.vendorOriginalPrice.value) *
                                                100,
                                            )}% below`}
                                          )
                                        </span>
                                      )}
                                      {option.sourceUrl && (
                                        <>
                                          {" · "}
                                          <a
                                            href={option.sourceUrl}
                                            target="_blank"
                                            rel="noreferrer"
                                            className="text-blue-700 hover:underline"
                                            onClick={(e) => e.stopPropagation()}
                                          >
                                            vendor page
                                          </a>
                                        </>
                                      )}
                                      {superUserActive && option.productId && (
                                        <>
                                          {" · "}
                                          <button
                                            type="button"
                                            className="font-medium text-indigo-700 hover:underline"
                                            title="Super powers: change this item's price, lead time, standard, sizes or other fields. Changes the inventory item itself."
                                            onClick={(e) => {
                                              e.preventDefault();
                                              e.stopPropagation();
                                              setEditingProductId(option.productId);
                                            }}
                                          >
                                            Edit item
                                          </button>
                                        </>
                                      )}
                                    </p>
                                    {option.learned && (
                                      <p
                                        className="mt-0.5 text-indigo-700"
                                        title="From the reasons operators gave when they chose vendors before."
                                      >
                                        {option.learned}
                                      </p>
                                    )}
                                  </div>
                                </label>
                              );
                            })}

                            {/* Why this vendor: kept with the pick and used
                                in later recommendations. */}
                            {isKonoTeam && currentId && chosenId && (
                              <div className="mt-2 rounded-lg border border-indigo-200 bg-indigo-50/50 p-2.5">
                                <label className="block text-[0.7rem] font-medium text-indigo-900">
                                  Why this vendor? (kept, and used in later
                                  recommendations)
                                </label>
                                <div className="mt-1 flex flex-wrap items-center gap-2">
                                  <input
                                    type="text"
                                    className="min-w-[16rem] flex-1 rounded-md border border-gray-300 px-2 py-1 text-xs"
                                    placeholder="Fastest lead time and they always send clean certs"
                                    value={
                                      pickReasons[entry.lineId] ??
                                      line?.preferredProductReason ??
                                      ""
                                    }
                                    onChange={(e) =>
                                      setPickReasons((prev) => ({
                                        ...prev,
                                        [entry.lineId]: e.target.value,
                                      }))
                                    }
                                  />
                                  <button
                                    type="button"
                                    disabled={savingPick === entry.lineId}
                                    onClick={() =>
                                      void saveVendorPick(entry.lineId, chosenId)
                                    }
                                    className="rounded-lg border border-indigo-300 bg-white px-3 py-1 text-xs font-medium text-indigo-800 hover:bg-indigo-50 disabled:opacity-50"
                                  >
                                    {savingPick === entry.lineId
                                      ? "Saving…"
                                      : "Save choice"}
                                  </button>
                                </div>
                              </div>
                            )}

                            {/* Vendor answers that arrived but cannot be
                                quoted as they stand. */}
                            {!!entry.rejectedVendorItems?.length && (
                              <div className="mt-2 rounded-lg border border-amber-200 bg-amber-50 p-2.5 text-[0.7rem] text-amber-900">
                                <p className="font-semibold">
                                  Vendor answers that cannot be quoted as they
                                  stand
                                </p>
                                <ul className="mt-1 list-disc pl-4">
                                  {entry.rejectedVendorItems.map((item) => (
                                    <li key={item.productId}>
                                      <span className="font-medium">
                                        {item.supplierName || "Unknown vendor"}
                                      </span>{" "}
                                      — {item.summary}
                                      {item.price ? ` (${item.price})` : ""}:{" "}
                                      {item.reasons.join("; ")}
                                    </li>
                                  ))}
                                </ul>
                              </div>
                            )}

                            {/* Vendors to RFQ — including exact inventory
                                matches (opt-in confirm) and LLM suggestions
                                when the catalog is thin. */}
                            <div className="mt-3 rounded-lg border border-gray-200 bg-gray-50/60 p-2.5">
                                <p className="text-xs font-semibold text-[#00062e]">
                                  Vendors to RFQ
                                </p>
                                <p className="mt-0.5 text-[0.7rem] text-gray-500">
                                  {entry.verdict === "exact"
                                    ? "This item is in inventory. Check the matching vendor to confirm availability, price, and lead time, and check others for alternative quotes. The customer is never named."
                                    : "Check every vendor who should receive an RFQ for this item — you can select more than one. Sorted closest to the request first. Suggested sources (not in our catalog) send a Kono introduction. The customer is never named."}
                                </p>
                                {entry.vendorSuggestions.length === 0 && (
                                  <p className="mt-1 text-xs text-gray-500">
                                    No catalog vendors or suggested sources
                                    found for this material.
                                  </p>
                                )}
                                {entry.vendorSuggestions
                                  .filter((vendor) => !vendor.secondary || showPossibleVendors[entry.lineId] || (Array.isArray(line?.vendorIdsToContact) && line.vendorIdsToContact.includes(vendor.supplierId)))
                                  .map((vendor) => {
                                  const explicit = line?.vendorIdsToContact;
                                  const checked = Array.isArray(explicit)
                                    ? explicit.includes(vendor.supplierId)
                                    : entry.verdict === "unavailable" &&
                                      vendor.suggested;
                                  const intro =
                                    llmIntros[vendor.supplierId] ||
                                    (vendor.introSubject && vendor.introBody
                                      ? {
                                          subject: vendor.introSubject,
                                          body: vendor.introBody,
                                        }
                                      : null);
                                  return (
                                    <div key={vendor.supplierId}>
                                      <label
                                        className={`mt-1.5 flex items-start gap-2 text-xs ${
                                          vendor.hasEmail && !vendor.excluded
                                            ? "cursor-pointer text-[#00062e]"
                                            : "text-gray-400"
                                        }`}
                                        title={
                                          vendor.excluded
                                            ? "This vendor's line card rules this material out"
                                            : undefined
                                        }
                                      >
                                        <input
                                          type="checkbox"
                                          className="mt-0.5"
                                          checked={checked}
                                          disabled={
                                            !vendor.hasEmail ||
                                            vendor.excluded ||
                                            vendorPickLocked
                                          }
                                          onChange={() => {
                                            const willCheck = !checked;
                                            toggleVendor(
                                              entry.lineId,
                                              entry.vendorSuggestions,
                                              entry.verdict,
                                              vendor.supplierId,
                                            );
                                            if (willCheck && needsPrepare(vendor.source)) {
                                              void prepareLlmVendorIfNeeded(
                                                line,
                                                vendor,
                                              );
                                            }
                                          }}
                                        />
                                        <span>
                                          <span className="font-medium">
                                            {vendor.name}
                                          </span>
                                          <span
                                            className={`ml-1 rounded-full px-1.5 py-0.5 text-[0.6rem] font-medium ${vendorSourceChipClass(
                                              vendor.source,
                                            )}`}
                                            title={vendorSourceTitle(vendor)}
                                          >
                                            {vendorSourceLabel(vendor)}
                                            {vendor.sourceUpdatedAt &&
                                              ` · updated ${formatSourceDate(
                                                vendor.sourceUpdatedAt,
                                              )}`}
                                          </span>
                                          <VendorProvenanceChip vendor={vendor} className="ml-1" />
                                          {vendor.secondary && (
                                            <span className="ml-1 rounded-full bg-gray-200 px-1.5 py-0.5 text-[0.6rem] font-medium text-gray-700" title="Not a clear source for this item: same alloy family, other forms, or a guess. Listed because you asked for the other possible options.">
                                              possible
                                            </span>
                                          )}
                                          {vendor.contactFrom && (
                                            <span className="ml-1 rounded-full bg-sky-100 px-1.5 py-0.5 text-[0.6rem] font-medium text-sky-800" title={`This record has no email of its own; the RFQ goes to the contacts of "${vendor.contactFrom}", linked as the same vendor (Catalogs > Vendors > Same Vendor accounts).`}>
                                              contact from {vendor.contactFrom}
                                            </span>
                                          )}
                                          {!vendor.hasEmail &&
                                            !vendor.excluded &&
                                            " (no email on file)"}
                                          <span
                                            className={
                                              vendor.excluded
                                                ? "text-red-700"
                                                : "text-gray-500"
                                            }
                                          >
                                            {" "}
                                            — {vendor.reasons.join("; ")}
                                          </span>
                                          {vendor.website && (
                                            <>
                                              {" · "}
                                              <a
                                                href={websiteHref(vendor.website)}
                                                target="_blank"
                                                rel="noreferrer"
                                                className="text-blue-700 hover:underline"
                                                onClick={(e) =>
                                                  e.stopPropagation()
                                                }
                                              >
                                                website
                                              </a>
                                            </>
                                          )}
                                          {vendor.phone && (
                                            <span className="text-gray-500">
                                              {" · "}
                                              {vendor.phone}
                                            </span>
                                          )}
                                        </span>
                                      </label>
                                      {!vendor.hasEmail && !vendorPickLocked && (
                                        <div className="ml-6 mt-1 flex items-center gap-2">
                                          <input
                                            type="email"
                                            className="w-56 rounded border border-gray-300 px-2 py-1 text-xs text-[#00062e] focus:border-teal-500 focus:outline-none"
                                            placeholder="sales@vendor.com"
                                            value={
                                              vendorEmailDrafts[
                                                vendor.supplierId
                                              ] ||
                                              vendor.email ||
                                              ""
                                            }
                                            onChange={(e) =>
                                              setVendorEmailDrafts((prev) => ({
                                                ...prev,
                                                [vendor.supplierId]:
                                                  e.target.value,
                                              }))
                                            }
                                          />
                                          <button
                                            onClick={() =>
                                              void addVendorEmail(
                                                entry.lineId,
                                                entry.vendorSuggestions,
                                                entry.verdict,
                                                vendor.supplierId,
                                              )
                                            }
                                            disabled={
                                              savingVendorEmail ===
                                                vendor.supplierId ||
                                              !(
                                                vendorEmailDrafts[
                                                  vendor.supplierId
                                                ] ||
                                                vendor.email ||
                                                ""
                                              ).includes("@")
                                            }
                                            className="rounded border border-gray-300 bg-white px-2 py-1 text-xs font-medium text-[#00062e] hover:bg-gray-50 disabled:opacity-50"
                                          >
                                            {savingVendorEmail ===
                                            vendor.supplierId
                                              ? "Saving..."
                                              : needsPrepare(vendor.source)
                                                ? "Save email & draft intro"
                                                : "Save email"}
                                          </button>
                                          {vendor.website && (
                                            <button
                                              type="button"
                                              onClick={() => void scanContactUs(entry.lineId, vendor)}
                                              disabled={scanningContactUs === vendor.supplierId}
                                              title="Read the vendor's website: the Contact page for a sales address (saved on the vendor when found), or the contact form filled with the outreach identity from Configuration > Inventory & Vendors"
                                              className="rounded border border-violet-300 bg-violet-50 px-2 py-1 text-xs font-medium text-violet-800 hover:bg-violet-100 disabled:opacity-50"
                                            >
                                              {scanningContactUs === vendor.supplierId ? "Reading the website…" : "Find contact on the website"}
                                            </button>
                                          )}
                                        </div>
                                      )}
                                      {(() => {
                                        const cu = contactUsScans[vendor.supplierId];
                                        if (!cu) return null;
                                        const form = cu.scan.form;
                                        return (
                                          <div className="ml-6 mt-1.5 rounded border border-violet-200 bg-white p-2 text-xs text-gray-700">
                                            {cu.scan.error ? (
                                              <p className="text-rose-700">{cu.scan.error}</p>
                                            ) : cu.savedEmail ? (
                                              <p className="text-emerald-800">
                                                Found <span className="font-medium">{cu.savedEmail}</span> on {cu.scan.contactUrl || cu.scan.website} and saved it on the vendor card. Tick the vendor to ask them.
                                              </p>
                                            ) : cu.scan.emails.length ? (
                                              <p>Found {cu.scan.emails.join(", ")} on the site; the first one is in the box above, press "Save email" to file it.</p>
                                            ) : form ? (
                                              <>
                                                <p className="font-medium text-violet-900">
                                                  No email on the site; a contact form was found on {cu.scan.contactUrl || cu.scan.website}. It is filled as {cu.identity.name}, {cu.identity.title} ({cu.identity.email}); edit and send.
                                                </p>
                                                {form.captcha && <p className="mt-1 text-rose-700">The form has a captcha: it cannot be sent from here. Open the page and paste the text below.</p>}
                                                <div className="mt-1 grid gap-1">
                                                  {form.fields.map((f) =>
                                                    f.type === "checkbox" ? (
                                                      <label key={f.name} className="flex items-center gap-1">
                                                        <input type="checkbox" checked={!!cu.values[f.name]} onChange={(e) => setContactUsScans((prev) => ({ ...prev, [vendor.supplierId]: { ...prev[vendor.supplierId], values: { ...prev[vendor.supplierId].values, [f.name]: e.target.checked ? "on" : "" } } }))} />
                                                        {f.label}
                                                      </label>
                                                    ) : (
                                                      <label key={f.name} className="grid gap-0.5">
                                                        <span className="text-[0.65rem] uppercase tracking-wide text-gray-500">
                                                          {f.label}
                                                          {f.required ? " *" : ""}
                                                        </span>
                                                        {f.type === "textarea" ? (
                                                          <textarea rows={5} className="rounded border border-gray-300 px-2 py-1 text-xs" value={cu.values[f.name] || ""} onChange={(e) => setContactUsScans((prev) => ({ ...prev, [vendor.supplierId]: { ...prev[vendor.supplierId], values: { ...prev[vendor.supplierId].values, [f.name]: e.target.value } } }))} />
                                                        ) : f.type === "select" ? (
                                                          <select className="rounded border border-gray-300 px-2 py-1 text-xs" value={cu.values[f.name] || ""} onChange={(e) => setContactUsScans((prev) => ({ ...prev, [vendor.supplierId]: { ...prev[vendor.supplierId], values: { ...prev[vendor.supplierId].values, [f.name]: e.target.value } } }))}>
                                                            <option value="">—</option>
                                                            {(f.options || []).map((o) => (
                                                              <option key={o} value={o}>
                                                                {o}
                                                              </option>
                                                            ))}
                                                          </select>
                                                        ) : (
                                                          <input className="rounded border border-gray-300 px-2 py-1 text-xs" value={cu.values[f.name] || ""} onChange={(e) => setContactUsScans((prev) => ({ ...prev, [vendor.supplierId]: { ...prev[vendor.supplierId], values: { ...prev[vendor.supplierId].values, [f.name]: e.target.value } } }))} />
                                                        )}
                                                      </label>
                                                    ),
                                                  )}
                                                </div>
                                                <div className="mt-2 flex flex-wrap items-center gap-2">
                                                  <button
                                                    type="button"
                                                    disabled={form.captcha || sendingContactUs === vendor.supplierId || !!cu.sent?.ok}
                                                    onClick={() => void sendContactUs(vendor)}
                                                    className="rounded bg-violet-700 px-2 py-1 text-xs font-medium text-white hover:bg-violet-800 disabled:opacity-50"
                                                  >
                                                    {sendingContactUs === vendor.supplierId ? "Sending…" : cu.sent?.ok ? "Sent" : "Send the Contact Us form"}
                                                  </button>
                                                  <a href={cu.scan.contactUrl || cu.scan.website} target="_blank" rel="noreferrer" className="text-blue-700 hover:underline">
                                                    open the page
                                                  </a>
                                                  {cu.sent && <span className={cu.sent.ok ? "text-emerald-800" : "text-rose-700"}>{cu.sent.note}</span>}
                                                </div>
                                              </>
                                            ) : (
                                              <p>
                                                Read {cu.scan.pagesRead.length} page{cu.scan.pagesRead.length === 1 ? "" : "s"} of {cu.scan.website}: no email and no contact form found.
                                                {cu.scan.phones.length ? ` Phone on the site: ${cu.scan.phones.join(", ")}.` : ""}
                                              </p>
                                            )}
                                          </div>
                                        );
                                      })()}
                                      {needsPrepare(vendor.source) && intro && (
                                        <div className="ml-6 mt-1.5 rounded border border-violet-200 bg-white p-2">
                                          <p className="text-[0.65rem] font-medium uppercase tracking-wide text-violet-800">
                                            Introduction email
                                          </p>
                                          <p className="mt-0.5 text-xs font-medium text-[#00062e]">
                                            {intro.subject}
                                          </p>
                                          <pre className="mt-1 max-h-40 overflow-auto whitespace-pre-wrap font-sans text-[0.7rem] text-gray-700">
                                            {intro.body}
                                          </pre>
                                          <button
                                            type="button"
                                            className="mt-1 inline-flex items-center gap-1 rounded border border-gray-300 bg-white px-2 py-0.5 text-[0.7rem] font-medium text-[#00062e] hover:bg-gray-50"
                                            onClick={() =>
                                              void navigator.clipboard.writeText(
                                                `Subject: ${intro.subject}\n\n${intro.body}`,
                                              )
                                            }
                                          >
                                            <ClipboardDocumentIcon className="h-3.5 w-3.5" />
                                            Copy
                                          </button>
                                        </div>
                                      )}
                                    </div>
                                  );
                                })}
                                {(() => {
                                  const hidden = entry.vendorSuggestions.filter((v) => v.secondary && !(Array.isArray(line?.vendorIdsToContact) && line.vendorIdsToContact.includes(v.supplierId)));
                                  if (!hidden.length) return null;
                                  const open = !!showPossibleVendors[entry.lineId];
                                  return (
                                    <button
                                      type="button"
                                      className="mt-2 rounded border border-gray-300 bg-white px-2 py-1 text-[0.7rem] font-medium text-[#00062e] hover:bg-gray-50"
                                      title="Vendors that might source this item (same alloy family, other forms, model guesses). The list above shows only the vendors that clearly source it because there are enough of them (the minimum is set in Vendor/Inventory Configuration)."
                                      onClick={() => setShowPossibleVendors((prev) => ({ ...prev, [entry.lineId]: !open }))}
                                    >
                                      {open ? "Hide the other possible options" : `Show other possible options (${hidden.length})`}
                                    </button>
                                  );
                                })()}
                                {!vendorPickLocked && (
                                  <div className="mt-2 border-t border-gray-200 pt-2">
                                    <label className="text-[0.7rem] font-medium text-gray-600">
                                      Add a vendor from the catalog
                                    </label>
                                    <p className="text-[0.65rem] text-gray-500">
                                      Not proposed above? Type part of the vendor&apos;s
                                      name; pick it to add it to this item&apos;s list
                                      and check it. Vendors without an email can be
                                      added and given one here.
                                    </p>
                                    <input
                                      type="search"
                                      className="mt-1 w-72 rounded border border-gray-300 px-2 py-1 text-xs text-[#00062e] focus:border-teal-500 focus:outline-none"
                                      placeholder="Vendor name (e.g. Fry Steel)"
                                      value={vendorSearch[entry.lineId]?.query || ""}
                                      onChange={(e) =>
                                        void searchCatalogVendors(entry.lineId, e.target.value)
                                      }
                                    />
                                    {vendorSearch[entry.lineId]?.searching && (
                                      <p className="mt-1 text-[0.7rem] text-gray-500">Searching...</p>
                                    )}
                                    {!vendorSearch[entry.lineId]?.searching &&
                                      (vendorSearch[entry.lineId]?.query || "").trim().length >= 2 &&
                                      vendorSearch[entry.lineId]?.hits.length === 0 && (
                                        <p className="mt-1 text-[0.7rem] text-gray-500">
                                          No catalog vendor with that name. Add it under
                                          Catalogs &gt; Vendors in the internal portal first.
                                        </p>
                                      )}
                                    {(vendorSearch[entry.lineId]?.hits || []).length > 0 && (
                                      <ul className="mt-1 max-h-48 w-full max-w-xl overflow-auto rounded border border-gray-200 bg-white">
                                        {vendorSearch[entry.lineId].hits.map((hit) => {
                                          const listed = entry.vendorSuggestions.some(
                                            (v) => v.supplierId === hit.supplierId,
                                          );
                                          return (
                                            <li
                                              key={hit.supplierId}
                                              className="flex items-center justify-between gap-2 border-b border-gray-100 px-2 py-1 text-xs last:border-b-0"
                                            >
                                              <span className="min-w-0 truncate">
                                                <span className="font-medium text-[#00062e]">{hit.name}</span>
                                                <span className="ml-1 text-gray-500">
                                                  {hit.hasEmail ? hit.email : "(no email on file)"}
                                                  {hit.country && hit.country !== "US" ? ` · ${hit.country}` : ""}
                                                </span>
                                              </span>
                                              <button
                                                type="button"
                                                className="shrink-0 rounded border border-gray-300 bg-white px-2 py-0.5 text-[0.7rem] font-medium text-[#00062e] hover:bg-gray-50 disabled:opacity-50"
                                                disabled={listed}
                                                onClick={() =>
                                                  addCatalogVendor(
                                                    entry.lineId,
                                                    entry.vendorSuggestions,
                                                    entry.verdict,
                                                    hit,
                                                  )
                                                }
                                              >
                                                {listed ? "Already listed" : hit.hasEmail ? "Add & check" : "Add"}
                                              </button>
                                            </li>
                                          );
                                        })}
                                      </ul>
                                    )}
                                  </div>
                                )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                  {editingProductId && superUserActive && (
                    <SuperUserPricePanel
                      productId={editingProductId}
                      onClose={() => setEditingProductId(null)}
                      onSaved={() => {
                        // The edit re-indexes search; give it a moment before
                        // re-reading the options so the new figures show.
                        setTimeout(() => void loadMatches(), 1500);
                      }}
                    />
                  )}
                </section>
              )}

              {/* kono.ai: draft the customer email for missing info */}
              {isKonoTeam && missingCount > 0 && (
                <section className="rounded-xl border border-blue-200 bg-blue-50/50 p-4">
                  <h3 className="text-sm font-semibold text-[#00062e]">
                    Ask your customer for the missing details
                  </h3>
                  <p className="mt-0.5 text-xs text-gray-600">
                    Some information is missing. As a Kono team member you can
                    draft an email to your customer confirming what we read and
                    asking for the rest — then save this RFQ and update it when
                    they answer.
                  </p>
                  <button
                    onClick={() => void showCustomerDraft()}
                    disabled={saving}
                    className="mt-2 rounded-lg border border-blue-300 bg-white px-3 py-1.5 text-sm font-medium text-blue-800 hover:bg-blue-50"
                  >
                    Draft customer email
                  </button>
                  {draft && (
                    <div className="mt-3 rounded-lg border border-gray-200 bg-white p-3">
                      <p className="text-xs font-medium text-gray-500">Subject</p>
                      <p className="text-sm text-[#00062e]">{draft.subject}</p>
                      <p className="mt-2 text-xs font-medium text-gray-500">Body</p>
                      <pre className="mt-1 whitespace-pre-wrap font-sans text-sm text-[#00062e]">
                        {draft.body}
                      </pre>
                      <div className="mt-2 flex gap-2">
                        <button
                          onClick={() => void copyDraft()}
                          className="inline-flex items-center gap-1 rounded-lg border border-gray-300 px-3 py-1.5 text-xs font-medium text-[#00062e] hover:bg-gray-50"
                        >
                          <ClipboardDocumentIcon className="h-4 w-4" />
                          {draftCopied ? "Copied" : "Copy"}
                        </button>
                        <a
                          href={`mailto:${encodeURIComponent(
                            customer.email,
                          )}?subject=${encodeURIComponent(
                            draft.subject,
                          )}&body=${encodeURIComponent(draft.body)}`}
                          className="rounded-lg border border-gray-300 px-3 py-1.5 text-xs font-medium text-[#00062e] hover:bg-gray-50"
                        >
                          Open in email app
                        </a>
                      </div>
                    </div>
                  )}
                </section>
              )}

              {isKonoTeam && outreachPreview && (
                <section className="rounded-xl border border-amber-200 bg-amber-50/50 p-4">
                  <h3 className="text-sm font-semibold text-[#00062e]">
                    Preview RFQ emails to vendors
                  </h3>
                  <p className="mt-1 text-xs text-gray-600">
                    From {outreachPreview.fromName} &lt;
                    {outreachPreview.fromEmail}&gt;
                    {outreachPreview.cc
                      ? `, copy to ${outreachPreview.cc}`
                      : ""}
                    . Several items for the same vendor are one email. Edit the recipient, subject, or
                    body, then send.
                  </p>
                  <div className="mt-3 flex flex-col gap-3">
                    {outreachPreview.drafts.map((draft) => (
                      <div
                        key={draft.supplierId}
                        className="rounded-lg border border-gray-200 bg-white p-3"
                      >
                        <div className="flex flex-wrap items-center justify-between gap-2">
                          <p className="text-sm font-medium text-[#00062e]">
                            {draft.supplierName}
                          </p>
                          {draft.sent ? (
                            <span className="text-xs font-medium text-teal-800">
                              Sent
                            </span>
                          ) : null}
                        </div>
                        <p
                          className={`mt-1 text-xs ${
                            draft.contactName ? "text-gray-500" : "text-amber-700"
                          }`}
                        >
                          {draft.contactName
                            ? `Addressed to ${draft.contactName} (the vendor's sales contact on file${draft.emails.length > 1 ? `; also sent to ${draft.emails.filter((e) => e !== draft.emails[0]).join(", ")}` : ""}).`
                            : draft.emails.length
                              ? `Sent to the company address ${draft.emails.join(", ")}; no named salesperson on this vendor record, so the email opens with a plain greeting. Add the salesperson (or tick "send RFQ" on one) in Catalogs > Vendors to address them by name.`
                              : "No email address on this vendor record or on a linked record of the same vendor."}
                          {draft.contactFrom && (
                            <span className="ml-1 rounded-full bg-sky-100 px-1.5 py-0.5 text-[0.65rem] font-medium text-sky-800" title="This record has no email of its own; the addresses come from another record of the same vendor (Catalogs > Vendors > Same Vendor accounts).">
                              contact information from the "{draft.contactFrom}" vendor card
                            </span>
                          )}
                        </p>
                        <label className="mt-2 block text-xs font-medium text-gray-500">
                          To
                          <input
                            className={`${inputCls} mt-1`}
                            placeholder="Several addresses: separate them with a comma or a semicolon"
                            value={draft.emailsText ?? draft.emails.join(", ")}
                            disabled={!!draft.sent}
                            onChange={(e) =>
                              patchOutreachDraft(draft.supplierId, {
                                emailsText: e.target.value,
                                emails: splitAddresses(e.target.value),
                              })
                            }
                            onBlur={() =>
                              patchOutreachDraft(draft.supplierId, {
                                emailsText: undefined,
                              })
                            }
                          />
                        </label>
                        <label className="mt-2 block text-xs font-medium text-gray-500">
                          Cc
                          <input
                            className={`${inputCls} mt-1`}
                            placeholder={
                              outreachPreview.cc
                                ? `${outreachPreview.cc} is copied automatically; add other addresses here, separated by commas`
                                : "Other addresses to copy, separated by commas"
                            }
                            value={draft.ccText ?? (draft.cc || []).join(", ")}
                            disabled={!!draft.sent}
                            onChange={(e) =>
                              patchOutreachDraft(draft.supplierId, {
                                ccText: e.target.value,
                                cc: splitAddresses(e.target.value),
                              })
                            }
                            onBlur={() =>
                              patchOutreachDraft(draft.supplierId, {
                                ccText: undefined,
                              })
                            }
                          />
                        </label>
                        <label className="mt-2 block text-xs font-medium text-gray-500">
                          Subject
                          <input
                            className={`${inputCls} mt-1`}
                            value={draft.subject}
                            disabled={!!draft.sent}
                            onChange={(e) =>
                              patchOutreachDraft(draft.supplierId, {
                                subject: e.target.value,
                              })
                            }
                          />
                        </label>
                        <label className="mt-2 block text-xs font-medium text-gray-500">
                          Body
                          <textarea
                            className={`${inputCls} mt-1 font-sans`}
                            rows={12}
                            value={draft.body}
                            disabled={!!draft.sent}
                            onChange={(e) =>
                              patchOutreachDraft(draft.supplierId, {
                                body: e.target.value,
                              })
                            }
                          />
                        </label>
                        <button
                          onClick={() =>
                            void sendOutreachDrafts([draft.supplierId])
                          }
                          disabled={
                            disableVendorRfqs ||
                            !!draft.sent ||
                            submitting ||
                            !!sendingOutreachId ||
                            !draft.emails.length ||
                            !draft.subject.trim() ||
                            !draft.body.trim()
                          }
                          className="mt-2 rounded-lg bg-amber-700 px-4 py-1.5 text-sm font-medium text-white hover:bg-amber-800 disabled:opacity-50"
                        >
                          {sendingOutreachId === draft.supplierId
                            ? "Sending..."
                            : draft.sent
                              ? "Sent"
                              : "Send"}
                        </button>
                        {!draft.sent && (
                          <button
                            type="button"
                            onClick={() => removeOutreachDraft(draft.supplierId)}
                            disabled={submitting || !!sendingOutreachId}
                            title="Leave this vendor out of this round. 'Send all remaining' will skip them; they are offered again next time."
                            className="ml-2 mt-2 rounded-lg border border-gray-300 px-4 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                          >
                            Delete
                          </button>
                        )}
                      </div>
                    ))}
                    {!outreachPreview.drafts.length && (
                      <p className="text-sm text-gray-600">
                        Every vendor was removed from this round. Close and reopen the preview to start over.
                      </p>
                    )}
                  </div>
                  {outreachPreview.drafts.some((draft) => !draft.sent) && (
                    <button
                      onClick={() =>
                        void sendOutreachDrafts(
                          outreachPreview.drafts
                            .filter((draft) => !draft.sent)
                            .map((draft) => draft.supplierId),
                        )
                      }
                      disabled={
                        disableVendorRfqs || submitting || !!sendingOutreachId
                      }
                      className="mt-3 rounded-lg bg-amber-700 px-5 py-2 text-sm font-medium text-white hover:bg-amber-800 disabled:opacity-50"
                    >
                      {sendingOutreachId === "all"
                        ? "Sending..."
                        : "Send all remaining"}
                    </button>
                  )}
                </section>
              )}

              {/* Save / submit */}
              <section className="flex flex-wrap items-center gap-3">
                {isKonoTeam && disableVendorRfqs && (
                  <p className="w-full rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">
                    Sending RFQs to suppliers is disabled in RFQs
                    Configuration. Vendor emails and follow-ups will not go
                    out until that checkbox is unchecked.
                  </p>
                )}
                <button
                  onClick={() => void save()}
                  disabled={saving || editingLocked || (!dirty && !!currentId)}
                  className="rounded-lg border border-gray-300 px-5 py-2 text-sm font-medium text-[#00062e] hover:bg-gray-50 disabled:opacity-50"
                >
                  {saving ? "Saving..." : currentId ? "Save changes" : "Save for later"}
                </button>
                {isKonoTeam ? (
                  <>
                    <button
                      onClick={() =>
                        void (currentStatus === "quoted" ||
                        (previewVendorEmails &&
                          (currentStatus === "draft" ||
                            currentStatus === "waiting-vendors"))
                          ? openVendorEmailPreview()
                          : submit("vendors"))
                      }
                      disabled={
                        disableVendorRfqs ||
                        !complete ||
                        submitting ||
                        saving ||
                        previewingOutreach ||
                        vendorPickLocked ||
                        (alreadySubmitted &&
                          currentStatus !== "waiting-vendors" &&
                          currentStatus !== "quoted")
                      }
                      title={
                        currentStatus === "quoted"
                          ? "Ask more vendors for this RFQ. Tick the vendors in Sourcing first; a reply that beats the quote shows up as a lower price option."
                          : undefined
                      }
                      className="rounded-lg bg-amber-700 px-5 py-2 text-sm font-medium text-white hover:bg-amber-800 disabled:opacity-50"
                    >
                      {previewingOutreach
                        ? "Preparing preview..."
                        : submitting
                          ? "Submitting..."
                          : currentStatus === "quoted"
                            ? "Ask more vendors"
                            : previewVendorEmails &&
                                (currentStatus === "draft" ||
                                  currentStatus === "waiting-vendors")
                              ? "Preview RFQs to Vendors"
                              : alreadySubmitted
                                ? "RFQ submitted"
                                : "Submit RFQ to vendors"}
                    </button>
                    <button
                      onClick={() => void submit("quote")}
                      disabled={
                        !complete ||
                        submitting ||
                        saving ||
                        editingLocked ||
                        alreadySubmitted
                      }
                      className="rounded-lg bg-[#00062e] px-5 py-2 text-sm font-medium text-white hover:bg-[#001858] disabled:opacity-50"
                    >
                      {submitting
                        ? "Submitting..."
                        : alreadySubmitted
                          ? "RFQ submitted"
                          : "Convert to quote"}
                    </button>
                  </>
                ) : (
                  <button
                    onClick={() => void submit("quote")}
                    disabled={
                      !complete ||
                      submitting ||
                      saving ||
                      editingLocked ||
                      alreadySubmitted
                    }
                    className="rounded-lg bg-[#00062e] px-5 py-2 text-sm font-medium text-white hover:bg-[#001858] disabled:opacity-50"
                    title={
                      complete
                        ? ""
                        : "Fill the missing fields on every item to submit"
                    }
                  >
                    {submitting
                      ? "Submitting..."
                      : alreadySubmitted
                        ? "RFQ submitted"
                        : "Submit RFQ"}
                  </button>
                )}
                {!complete && hasWork && (
                  <p className="text-xs text-red-600">
                    {missingCount} detail{missingCount === 1 ? "" : "s"} still
                    missing — you can save now and finish later.
                  </p>
                )}
                {alreadySubmitted && (
                  <p className="w-full text-sm text-teal-800">
                    {outcome?.outcome === "waiting-vendors"
                      ? isKonoTeam
                        ? "Submitted. Scroll up — vendors are being asked. Generate the quote when you are ready."
                        : `Submitted. We are sourcing this from our vendors and will email your complete quote within ${
                            outcome.maxQuoteBusinessHours
                          } business hours${
                            deadlineText ? ` — by ${deadlineText}` : ""
                          }.`
                      : outcome?.outcome === "quoted"
                        ? "Submitted. Scroll up to open your quote."
                        : outcome?.outcome === "quoted-partial"
                          ? "Submitted. Scroll up to see what is available now and what we are still sourcing."
                          : outcome?.outcome === "choose-options"
                            ? "Submitted. Scroll up to choose the option you want for each item."
                            : "This RFQ has already been submitted. Scroll up for the latest status."}
                  </p>
                )}
                {complete && isKonoTeam && !alreadySubmitted && (
                  <div className="w-full max-w-2xl space-y-1 text-xs text-gray-600">
                    <p>
                      <span className="font-medium text-[#00062e]">
                        {previewVendorEmails
                          ? "Preview RFQs to Vendors"
                          : "Submit RFQ to vendors"}
                      </span>
                      {" — "}
                      {previewVendorEmails
                        ? "Shows each vendor email so you can edit the recipient, subject, and body before sending. Emails "
                        : "Emails "}
                      the checked vendors for each item (you can check
                      more than one), including a perfect inventory match to
                      confirm availability, price, and lead time. Suggested
                      sources get a Kono introduction. Does not build a
                      customer quote yet; replies and the deadline still wait
                      for you to generate it.
                    </p>
                    <p>
                      <span className="font-medium text-[#00062e]">
                        Convert to quote
                      </span>
                      {" — "}
                      builds a quote only from items that are in inventory
                      exactly as requested, or close matches you marked as
                      replacements. It never emails vendors. If nothing is
                      quoteable, you will be asked to select replacements
                      or send RFQs to vendors first.
                    </p>
                  </div>
                )}
              </section>
            </div>
          )}
        </main>
      </div>
    </div>
  );
}
